import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { 
  CalendarIcon,
  Download,
  FileText,
  TrendingUp,
  RefreshCw,
  AlertTriangle,
  FileDown,
  Filter,
  Check,
  Share2,
  Mail
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(value || 0);
};

// WhatsApp share helper - opens WhatsApp with pre-filled message
const shareViaWhatsApp = (title, content) => {
  const message = `📊 *${title}*\n\n${content}\n\n_Generated by PROMORTEK Sales Management System_`;
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
  window.open(whatsappUrl, '_blank');
};

// Email share helper - opens default email client
const shareViaEmail = (title, content, storeName = '') => {
  const subject = encodeURIComponent(`${title}${storeName ? ` - ${storeName}` : ''}`);
  const body = encodeURIComponent(`${content}\n\n--\nGenerated by PROMORTEK Sales Management System`);
  const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
  window.location.href = mailtoUrl;
};

export const ReportsPage = () => {
  const { isAdmin, isManager, isSuperUser } = useAuth();
  const { stores, setSelectedStore } = useStore();
  const [loading, setLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [reportType, setReportType] = useState('daily');
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 7),
    end: new Date()
  });

  // Auto-select first store for non-super users
  useEffect(() => {
    if (!isSuperUser && stores.length > 0 && selectedStoreId === 'all') {
      setSelectedStoreId(stores[0].id);
    }
  }, [stores, isSuperUser, selectedStoreId]);

  // Report data
  const [dailySummary, setDailySummary] = useState(null);
  const [storeSummary, setStoreSummary] = useState([]);
  const [varianceReport, setVarianceReport] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [compareData, setCompareData] = useState(null);
  
  // New report data
  const [creditorReport, setCreditorReport] = useState([]);
  const [investmentReport, setInvestmentReport] = useState([]);
  const [cashControlReport, setCashControlReport] = useState([]);
  const [purchaseReport, setPurchaseReport] = useState([]);
  const [scratchSettlementReport, setScratchSettlementReport] = useState([]);
  const [scratchSalesReport, setScratchSalesReport] = useState([]);
  const [fuelDeliveryReport, setFuelDeliveryReport] = useState([]);
  const [fuelSalesReport, setFuelSalesReport] = useState([]);
  const [fuelPriceReport, setFuelPriceReport] = useState([]);
  const [fuelProfitReport, setFuelProfitReport] = useState([]);
  const [coffeeTrackingReport, setCoffeeTrackingReport] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Field filters for reports
  const [selectedFields, setSelectedFields] = useState({});
  const [showFieldFilter, setShowFieldFilter] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);

  // Fuel grade filter
  const [selectedFuelGrade, setSelectedFuelGrade] = useState('all');

  // Coffee cup size filter
  const [selectedCupSize, setSelectedCupSize] = useState('all');

  // Purchase type filter
  const [purchaseTypeFilter, setPurchaseTypeFilter] = useState('all');
  const [purchaseTypes, setPurchaseTypes] = useState([]);

  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);

  // Period comparison
  const [period1, setPeriod1] = useState({
    start: startOfWeek(subDays(new Date(), 14), { weekStartsOn: 1 }),
    end: endOfWeek(subDays(new Date(), 14), { weekStartsOn: 1 })
  });
  const [period2, setPeriod2] = useState({
    start: startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 }),
    end: endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 })
  });

  // Creditor/Scheme filters for reports
  const [creditors, setCreditors] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [selectedCreditor, setSelectedCreditor] = useState('all');
  const [selectedScheme, setSelectedScheme] = useState('all');
  
  // Fuel card types for fuel station column display
  const [fuelCardTypes, setFuelCardTypes] = useState([]);
  
  // Creditor report additional filters
  const [creditorTransactionType, setCreditorTransactionType] = useState('all'); // 'all', 'credit', 'repayment'
  const [showFromLastPayment, setShowFromLastPayment] = useState(false);

  // Fetch creditors and schemes for filters
  useEffect(() => {
    const fetchCreditorData = async () => {
      try {
        const storeId = selectedStoreId === 'all' ? '' : selectedStoreId;
        const [creditorsRes, schemesRes] = await Promise.all([
          axios.get(`${API}/creditors/creditors`, { params: { store_id: storeId || undefined } }),
          axios.get(`${API}/creditors/schemes`, { params: { store_id: storeId || undefined } })
        ]);
        setCreditors(creditorsRes.data || []);
        setSchemes(schemesRes.data || []);
      } catch (error) {
        console.error('Failed to fetch creditors/schemes');
      }
    };
    fetchCreditorData();
  }, [selectedStoreId]);

  // Fetch users for name display
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/users`);
        setUsers(res.data);
      } catch (error) {
        console.error('Failed to fetch users');
      }
    };
    fetchUsers();
  }, []);

  // Fetch purchase types for filter
  useEffect(() => {
    const fetchPurchaseTypes = async () => {
      try {
        const res = await axios.get(`${API}/settings/purchase-types`);
        setPurchaseTypes(res.data || []);
      } catch (error) {
        console.error('Failed to fetch purchase types');
      }
    };
    fetchPurchaseTypes();
  }, []);

  // Fetch fuel card types for fuel station reports
  useEffect(() => {
    const fetchFuelCardTypes = async () => {
      try {
        const res = await axios.get(`${API}/settings/fuel-card-types`);
        setFuelCardTypes((res.data || []).filter(fc => fc.is_active));
      } catch (error) {
        console.error('Failed to fetch fuel card types');
      }
    };
    fetchFuelCardTypes();
  }, []);

  // Helper to get user name from ID
  const getUserName = (userId) => {
    if (!userId) return '-';
    // Handle special super user ID
    if (userId === 'super-user-001') return 'Super User';
    // Find user in users array
    const user = users.find(u => u.id === userId);
    if (user) return user.name || user.email;
    // If not found, return a shortened version of the ID
    if (userId.length > 20) return userId.substring(0, 8) + '...';
    return userId;
  };

  // Get available fuel grades from selected store(s)
  const getAvailableFuelGrades = () => {
    const gradesMap = new Map();
    const relevantStores = selectedStoreId === 'all' 
      ? stores.filter(s => s.store_type === 'fuel_station')
      : stores.filter(s => s.id === selectedStoreId && s.store_type === 'fuel_station');
    
    relevantStores.forEach(store => {
      (store.fuel_grades || []).forEach(grade => {
        if (!gradesMap.has(grade.name)) {
          gradesMap.set(grade.name, { id: grade.id, name: grade.name });
        }
      });
    });
    
    return Array.from(gradesMap.values());
  };

  const availableFuelGrades = getAvailableFuelGrades();

  // Reset fuel grade filter when store changes
  useEffect(() => {
    setSelectedFuelGrade('all');
  }, [selectedStoreId]);

  // Reset field filters when report type changes
  useEffect(() => {
    setSelectedFields({});
    setShowFieldFilter(false);
  }, [reportType]);

  useEffect(() => {
    fetchReports();
  }, [selectedStoreId, dateRange, reportType]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const storeId = selectedStoreId !== 'all' ? selectedStoreId : undefined;

      if (reportType === 'daily') {
        const res = await axios.get(`${API}/reports/daily-summary`, {
          params: { 
            store_id: storeId, 
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setDailySummary(res.data);
      } else if (reportType === 'store') {
        const res = await axios.get(`${API}/reports/store-summary`, {
          params: {
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setStoreSummary(res.data);
      } else if (reportType === 'variance') {
        const res = await axios.get(`${API}/reports/variance-report`, {
          params: {
            store_id: storeId,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setVarianceReport(res.data);
      } else if (reportType === 'trend') {
        const res = await axios.get(`${API}/reports/sales-trend`, {
          params: { store_id: storeId, days: 30 }
        });
        setSalesTrend(res.data);
      } else if (reportType === 'lending') {
        const res = await axios.get(`${API}/creditors/transactions`, {
          params: { 
            store_id: storeId, 
            transaction_type: 'lending',
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setCreditorReport(res.data || []);
      } else if (reportType === 'investments') {
        const res = await axios.get(`${API}/creditors/transactions`, {
          params: { 
            store_id: storeId, 
            transaction_type: 'investment',
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setInvestmentReport(res.data || []);
      } else if (reportType === 'cashcontrol') {
        const res = await axios.get(`${API}/money-movements`, {
          params: { 
            store_id: storeId,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setCashControlReport(res.data || []);
      } else if (reportType === 'purchases') {
        const res = await axios.get(`${API}/purchases`, {
          params: { 
            store_id: storeId,
            purchase_type: purchaseTypeFilter !== 'all' ? purchaseTypeFilter : undefined,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        setPurchaseReport(res.data || []);
      } else if (reportType === 'scratch_settlements') {
        const res = await axios.get(`${API}/scratch-cards/settlements`, { 
          params: { 
            store_id: storeId,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          } 
        });
        setScratchSettlementReport(res.data || []);
      } else if (reportType === 'scratch_sales') {
        // Get scratch card sales from daily entries
        const res = await axios.get(`${API}/sales`, {
          params: { 
            store_id: storeId,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        const salesData = (res.data || []).filter(e => (e.service_amounts?.scratch_cards > 0)).map(e => ({
          date: e.sales_date,
          shift: e.shift,
          scratch_sales: e.service_amounts?.scratch_cards || 0,
          scratch_prize_paid: e.scratch_card_prize_paid || 0,
          submitted_by: e.submitted_by
        }));
        setScratchSalesReport(salesData);
      } else if (reportType === 'fuel_deliveries') {
        const res = await axios.get(`${API}/fuel/deliveries`, {
          params: { store_id: storeId }
        });
        // Flatten the delivery data - each grade becomes a separate row
        const flattenedData = [];
        (res.data || []).forEach(delivery => {
          (delivery.grades || []).forEach(grade => {
            flattenedData.push({
              date: delivery.delivery_date,
              grade_name: grade.grade_name,
              quantity: grade.quantity,
              cost_per_litre: grade.cost_per_litre, // Already in pennies
              total_cost: grade.total_cost / 100, // Convert from pennies to pounds
              invoice_number: delivery.invoice_number,
              supplier: delivery.supplier,
              user_id: delivery.created_by
            });
          });
        });
        setFuelDeliveryReport(flattenedData);
      } else if (reportType === 'fuel_sales') {
        // Get fuel sales from daily entries - break down by grade
        const res = await axios.get(`${API}/sales`, {
          params: { store_id: storeId }
        });
        
        // Get all store data to map grade IDs to names
        const storeData = selectedStoreId === 'all' 
          ? stores.filter(s => s.store_type === 'fuel_station')
          : stores.filter(s => s.id === selectedStoreId);
        
        const gradeNameMap = new Map();
        storeData.forEach(store => {
          (store.fuel_grades || []).forEach(grade => {
            gradeNameMap.set(grade.id, grade.name);
          });
        });
        
        const fuelSalesData = [];
        (res.data || []).filter(e => e.fuel_volumes && Object.keys(e.fuel_volumes).length > 0).forEach(e => {
          // Create one entry per grade per sales entry
          Object.keys(e.fuel_volumes || {}).forEach(gradeId => {
            const volume = parseFloat(e.fuel_volumes[gradeId]) || 0;
            const price = parseFloat(e.fuel_prices?.[gradeId]) || 0; // price in pennies
            if (volume > 0) {
              fuelSalesData.push({
                date: e.sales_date,
                shift: e.shift,
                grade_id: gradeId,
                grade_name: gradeNameMap.get(gradeId) || `Grade ${gradeId}`,
                total_volume: volume,
                total_sales: (volume * price) / 100, // Convert to pounds (price is in pennies)
                price_per_litre: price, // Keep in pennies for display
                submitted_by: e.submitted_by
              });
            }
          });
        });
        setFuelSalesReport(fuelSalesData);
      } else if (reportType === 'fuel_prices') {
        // Get fuel price history for fuel stations
        const fuelStores = selectedStoreId === 'all'
          ? stores.filter(s => s.store_type === 'fuel_station')
          : stores.filter(s => s.id === selectedStoreId && s.store_type === 'fuel_station');
        
        if (fuelStores.length === 0) {
          setFuelPriceReport([]);
          toast.info('No fuel stations found for the selected store(s)');
        } else {
          const allPriceData = [];
          for (const store of fuelStores) {
            try {
              const res = await axios.get(`${API}/fuel/daily-prices/range`, {
                params: {
                  store_id: store.id,
                  start_date: format(dateRange.start, 'yyyy-MM-dd'),
                  end_date: format(dateRange.end, 'yyyy-MM-dd')
                }
              });
              
              // Add store name and format data
              (res.data || []).forEach(p => {
                allPriceData.push({
                  ...p,
                  store_name: store.name,
                  margin: p.selling_price && p.cost_price ? (p.selling_price - p.cost_price).toFixed(2) : '-'
                });
              });
            } catch (err) {
              console.error(`Failed to fetch prices for ${store.name}:`, err);
            }
          }
          
          // Sort by date descending, then by store
          allPriceData.sort((a, b) => {
            if (a.date !== b.date) return b.date.localeCompare(a.date);
            return a.store_name.localeCompare(b.store_name);
          });
          
          setFuelPriceReport(allPriceData);
        }
      } else if (reportType === 'fuel_profit') {
        // Get fuel profit report from sales entries with fuel_margins data
        const res = await axios.get(`${API}/sales`, {
          params: { 
            store_id: storeId,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        
        const entries = res.data || [];
        const profitData = [];
        
        // Process entries that have fuel data
        entries.forEach(e => {
          if (e.fuel_volumes && Object.keys(e.fuel_volumes).length > 0) {
            const store = stores.find(s => s.id === e.store_id);
            const fuelGrades = store?.fuel_grades || [];
            
            // Get or calculate margins/profits
            let totalProfit = e.total_fuel_profit || 0;
            let gradeDetails = [];
            
            // Build grade-level details
            Object.keys(e.fuel_volumes).forEach(gradeId => {
              const volume = parseFloat(e.fuel_volumes[gradeId]) || 0;
              const cost = parseFloat(e.fuel_costs?.[gradeId]) || 0;
              const price = parseFloat(e.fuel_prices?.[gradeId]) || 0;
              const margin = e.fuel_margins?.[gradeId] || (price - cost);
              const profit = e.fuel_profits?.[gradeId] || (volume * margin / 100);
              
              const gradeName = fuelGrades.find(g => g.id === gradeId)?.name || gradeId;
              
              if (volume > 0) {
                gradeDetails.push({
                  grade_id: gradeId,
                  grade_name: gradeName,
                  volume: volume,
                  cost: cost,
                  price: price,
                  margin: margin,
                  profit: profit
                });
                
                // Recalculate total if not stored
                if (!e.total_fuel_profit) {
                  totalProfit += profit;
                }
              }
            });
            
            if (gradeDetails.length > 0) {
              profitData.push({
                date: e.sales_date,
                shift: e.shift,
                store_id: e.store_id,
                store_name: store?.name || e.store_id,
                grades: gradeDetails,
                total_volume: gradeDetails.reduce((sum, g) => sum + g.volume, 0),
                total_profit: totalProfit
              });
            }
          }
        });
        
        // Sort by date descending
        profitData.sort((a, b) => b.date.localeCompare(a.date));
        setFuelProfitReport(profitData);
      } else if (reportType === 'coffee_tracking') {
        // Get coffee tracking data from daily entries with date filtering
        const res = await axios.get(`${API}/sales`, {
          params: { 
            store_id: storeId,
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd')
          }
        });
        
        // Also ensure users are loaded for name lookup
        let usersList = users;
        if (usersList.length === 0) {
          try {
            const usersRes = await axios.get(`${API}/users`);
            usersList = usersRes.data || [];
            setUsers(usersList);
          } catch (e) {
            console.error('Failed to fetch users');
          }
        }
        
        // Helper to get user name
        const lookupUserName = (userId) => {
          if (!userId) return '-';
          if (userId === 'super-user-001') return 'Super User';
          const user = usersList.find(u => u.id === userId);
          return user ? (user.name || user.email) : '-';
        };
        
        const coffeeData = [];
        (res.data || []).forEach(e => {
          const ct = e.coffee_tracking;
          if (!ct) return;
          
          // POS sold quantity is at root level as coffee_sold_qty
          const posQty = parseInt(ct.coffee_sold_qty) || 0;
          const submitterName = lookupUserName(e.submitted_by || e.created_by);
          
          // Check for small cup data
          if (ct.small && (ct.small.opening_reading || ct.small.closing_reading)) {
            const opening = parseInt(ct.small.opening_reading) || 0;
            const closing = parseInt(ct.small.closing_reading) || 0;
            // Machine counter increments, so Machine Sold = Closing - Opening
            const machineSold = closing > opening ? closing - opening : 0;
            
            coffeeData.push({
              date: e.sales_date,
              shift: e.shift,
              cup_size: 'Small',
              opening_reading: opening,
              closing_reading: closing,
              machine_sold: machineSold,
              pos_qty: null, // Small doesn't have individual POS qty
              variance: null,
              user_id: e.created_by,
              submitted_by_name: submitterName
            });
          }
          
          // Check for large cup data
          if (ct.large && (ct.large.opening_reading || ct.large.closing_reading)) {
            const opening = parseInt(ct.large.opening_reading) || 0;
            const closing = parseInt(ct.large.closing_reading) || 0;
            // Machine counter increments, so Machine Sold = Closing - Opening
            const machineSold = closing > opening ? closing - opening : 0;
            
            coffeeData.push({
              date: e.sales_date,
              shift: e.shift,
              cup_size: 'Large',
              opening_reading: opening,
              closing_reading: closing,
              machine_sold: machineSold,
              pos_qty: null, // Large doesn't have individual POS qty
              variance: null,
              user_id: e.created_by,
              submitted_by_name: submitterName
            });
          }
          
          // Add a summary row with totals and POS qty
          const totalMachineSold = ct.total_machine_sold || 
            ((ct.small ? (parseInt(ct.small.closing_reading) || 0) - (parseInt(ct.small.opening_reading) || 0) : 0) +
             (ct.large ? (parseInt(ct.large.closing_reading) || 0) - (parseInt(ct.large.opening_reading) || 0) : 0));
          
          if (posQty > 0 || totalMachineSold > 0) {
            // Always calculate variance as POS Qty - Machine Sold
            // Negative = shortage (machine sold more than POS recorded)
            // Positive = surplus (POS recorded more than machine sold)
            const calculatedVariance = posQty - totalMachineSold;
            
            coffeeData.push({
              date: e.sales_date,
              shift: e.shift,
              cup_size: 'Total',
              opening_reading: '-',
              closing_reading: '-',
              machine_sold: totalMachineSold > 0 ? totalMachineSold : (ct.total_machine_sold || 0),
              pos_qty: posQty,
              variance: calculatedVariance,  // Variance = POS Qty - Machine Sold (negative when shortage)
              user_id: e.created_by,
              submitted_by_name: submitterName
            });
          }
        });
        
        // Sort by date descending, then by cup_size (Total first)
        coffeeData.sort((a, b) => {
          const dateCompare = b.date.localeCompare(a.date);
          if (dateCompare !== 0) return dateCompare;
          if (a.cup_size === 'Total') return -1;
          if (b.cup_size === 'Total') return 1;
          return a.cup_size.localeCompare(b.cup_size);
        });
        
        setCoffeeTrackingReport(coffeeData);
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/compare`, {
        params: {
          store_id: selectedStoreId !== 'all' ? selectedStoreId : undefined,
          period1_start: format(period1.start, 'yyyy-MM-dd'),
          period1_end: format(period1.end, 'yyyy-MM-dd'),
          period2_start: format(period2.start, 'yyyy-MM-dd'),
          period2_end: format(period2.end, 'yyyy-MM-dd')
        }
      });
      setCompareData(res.data);
    } catch (error) {
      toast.error('Failed to load comparison');
    } finally {
      setLoading(false);
    }
  };

  // Helper to flatten objects for export
  const flattenForExport = (data) => {
    if (!data || data.length === 0) return [];
    
    return data.map(row => {
      const flattened = {};
      Object.entries(row).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          flattened[key] = '';
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          // Flatten object to JSON string or summarize
          const entries = Object.entries(value);
          if (entries.length > 0) {
            flattened[key] = entries.map(([k, v]) => `${k}: ${v}`).join('; ');
          } else {
            flattened[key] = '';
          }
        } else if (Array.isArray(value)) {
          flattened[key] = value.length > 0 ? JSON.stringify(value) : '';
        } else {
          flattened[key] = value;
        }
      });
      return flattened;
    });
  };

  const exportCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const flatData = flattenForExport(data);
    const headers = Object.keys(flatData[0]);
    
    // Escape values for CSV (handle commas, quotes, newlines)
    const escapeCSV = (value) => {
      const str = String(value ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    const headerRow = headers.join(',');
    const rows = flatData.map(row => 
      headers.map(h => escapeCSV(row[h])).join(',')
    ).join('\n');
    const csv = `${headerRow}\n${rows}`;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const exportPDF = (data, title, filename, includeShopDetails = true) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const doc = generatePDFDocument(data, title, includeShopDetails, filename);
    doc.save(`${filename}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exported successfully');
  };

  // Generate PDF document (reusable for both download and email)
  const generatePDFDocument = (data, title, includeShopDetails = true, filename = '') => {
    const flatData = flattenForExport(data);
    // Landscape orientation
    const doc = new jsPDF('landscape');
    
    // Check if this is a coffee tracking report (variance should not be formatted as currency)
    const isCoffeeReport = filename.includes('coffee');
    
    // Get store details
    const store = selectedStoreId === 'all' 
      ? null 
      : stores.find(s => s.id === selectedStoreId);
    const storeName = store?.name || 'All Stores';
    const storeType = store?.store_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    const storeAddress = store?.address || '';
    
    let yPos = 15;
    
    // Shop Details Header (if single store selected)
    if (includeShopDetails && store) {
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, doc.internal.pageSize.width, 35, 'F');
      
      doc.setFontSize(20);
      doc.setTextColor(255);
      doc.text(storeName, 14, 15);
      
      doc.setFontSize(10);
      doc.text(`${storeType}${storeAddress ? ' | ' + storeAddress : ''}`, 14, 24);
      doc.text(`Generated: ${format(new Date(), 'PPP pp')}`, 14, 31);
      
      yPos = 45;
    } else {
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Store: ${storeName}`, 14, yPos);
      doc.text(`Generated: ${format(new Date(), 'PPP pp')}`, 14, yPos + 6);
      yPos += 15;
    }
    
    // Report Title
    doc.setFontSize(16);
    doc.setTextColor(40);
    doc.text(title, 14, yPos);
    yPos += 8;
    
    // Date Range
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date Range: ${format(dateRange.start, 'PP')} - ${format(dateRange.end, 'PP')}`, 14, yPos);
    yPos += 10;
    
    // Prepare table data
    const headers = Object.keys(flatData[0]);
    const rows = flatData.map(row => 
      headers.map(header => {
        const value = row[header];
        const headerLower = header.toLowerCase();
        
        // For coffee report, variance is cups count, not currency
        if (isCoffeeReport && headerLower.includes('variance')) {
          return value !== null && value !== undefined ? value.toString() : '-';
        }
        
        if (typeof value === 'number' && (headerLower.includes('amount') || headerLower.includes('sales') || headerLower.includes('total') || headerLower.includes('variance') || headerLower.includes('cost') || headerLower.includes('value') || headerLower.includes('net') || headerLower.includes('vat'))) {
          return formatCurrency(value);
        }
        return value?.toString() || '';
      })
    );
    
    // Calculate totals for numeric columns
    const totals = headers.map(header => {
      const headerLower = header.toLowerCase();
      const isNumeric = headerLower.includes('amount') || 
                       headerLower.includes('sales') || 
                       headerLower.includes('total') || 
                       headerLower.includes('variance') ||
                       headerLower.includes('cost') ||
                       headerLower.includes('value') ||
                       headerLower.includes('net') ||
                       headerLower.includes('vat') ||
                       headerLower.includes('quantity') ||
                       headerLower === 'credit' ||
                       headerLower === 'repayment' ||
                       headerLower === 'cash' ||
                       headerLower === 'card' ||
                       headerLower === 'amex' ||
                       headerLower === 'vouchers' ||
                       headerLower === 'opening_reading' ||
                       headerLower === 'closing_reading' ||
                       headerLower === 'machine_sold' ||
                       headerLower === 'pos_qty';
      if (isNumeric) {
        const sum = flatData.reduce((acc, row) => {
          const rawVal = row[header];
          // Strip all non-numeric characters except decimal point and minus sign
          const cleanVal = typeof rawVal === 'string' ? rawVal.replace(/[^0-9.-]/g, '') : rawVal;
          const val = parseFloat(cleanVal) || 0;
          return acc + val;
        }, 0);
        // For coffee report variance or quantity fields, show as plain number
        if (isCoffeeReport && (headerLower.includes('variance') || headerLower.includes('reading') || headerLower.includes('sold') || headerLower.includes('qty'))) {
          return sum.toString();
        }
        return headerLower.includes('quantity') ? sum.toLocaleString() : formatCurrency(sum);
      }
      return '';
    });
    totals[0] = 'TOTALS';
    
    // Find variance column index for conditional styling
    const varianceColIndex = headers.findIndex(h => h.toLowerCase().includes('variance'));
    
    // Add table using autoTable with totals as foot (only on last page)
    autoTable(doc, {
      head: [headers.map(h => h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))],
      body: rows,
      foot: [totals],
      startY: yPos,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        textColor: [50, 50, 50]
      },
      footStyles: {
        fillColor: [40, 40, 40],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8
      },
      showFoot: 'lastPage',  // Only show totals on the last page
      // Apply red color to negative variance values
      didParseCell: function(data) {
        if (data.section === 'body' && varianceColIndex >= 0 && data.column.index === varianceColIndex) {
          const cellValue = parseFloat(data.cell.raw);
          if (!isNaN(cellValue) && cellValue < 0) {
            data.cell.styles.textColor = [220, 38, 38]; // Red color for negative values
            data.cell.styles.fontStyle = 'bold';
          }
        }
        // Also style footer variance if negative
        if (data.section === 'foot' && varianceColIndex >= 0 && data.column.index === varianceColIndex) {
          const cellValue = parseFloat(data.cell.raw);
          if (!isNaN(cellValue) && cellValue < 0) {
            data.cell.styles.textColor = [252, 165, 165]; // Lighter red for footer (on dark bg)
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });
    
    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `PROMORTEK Sales Management System - Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    return doc;
  };

  // Get available fields for current report type
  const getAvailableFields = () => {
    // Get the selected store configuration
    const selectedStore = selectedStoreId !== 'all' ? stores.find(s => s.id === selectedStoreId) : null;
    
    // If a specific store is selected, filter based on its config
    // If selectedStoreId is 'all' OR store not found in array, show all fields
    const showAllFields = selectedStoreId === 'all' || !selectedStore;
    
    // Helper to check if a service/feature is enabled for the selected store
    const isServiceEnabled = (serviceKey) => {
      if (showAllFields) return true; // All stores - show all fields
      return (selectedStore.services || []).includes(serviceKey) || 
             (selectedStore.enabled_services || []).some(s => s.key === serviceKey);
    };
    
    // Helper to check if store is a fuel station
    const isFuelStation = () => {
      if (showAllFields) return true; // All stores - show fuel fields
      return selectedStore.store_type === 'fuel_station';
    };
    
    // Helper to check if coffee tracking is enabled
    const isCoffeeEnabled = () => {
      if (showAllFields) return true; // All stores - show coffee fields
      return selectedStore.coffee_tracking?.coffee_sold_enabled === true;
    };
    
    // Helper to check if voucher is enabled for the store
    const isVoucherEnabled = () => {
      if (showAllFields) return true;
      return selectedStore.voucher_enabled === true;
    };
    
    // Helper to check if AMEX is enabled for the store
    const isAmexEnabled = () => {
      if (showAllFields) return true;
      return selectedStore.amex_tracking_enabled === true;
    };
    
    // Helper to check if store has staff expenses payment type
    const hasStaffExpenses = () => {
      if (showAllFields) return true;
      return (selectedStore.payment_types || []).some(pt => 
        pt.name?.toLowerCase().includes('staff') || pt.key?.toLowerCase().includes('staff')
      );
    };
    
    // Helper to get active fuel cards for a fuel station
    const getActiveFuelCards = () => {
      if (!isFuelStation()) return [];
      if (showAllFields) return fuelCardTypes; // Show all fuel card types
      return fuelCardTypes.filter(fc => 
        (selectedStore.active_fuel_cards || []).some(c => c.card_type_id === fc.id)
      );
    };
    
    switch (reportType) {
      case 'daily':
        // Standard fields always shown
        const standardFields = [
          { key: 'store', label: 'Store' },
          { key: 'date', label: 'Date' },
          { key: 'shift', label: 'Shift' },
          { key: 'total_sales', label: 'Total Sales' },
          { key: 'shop_sales', label: 'Shop Sales' },
          { key: 'cash', label: 'Cash' },
          { key: 'card', label: 'Card (BMS)' },
        ];
        
        // Fuel Sales - only for fuel stations
        if (isFuelStation()) {
          standardFields.splice(4, 0, { key: 'fuel_sales', label: 'Fuel Sales' });
        }
        
        // Conditional payment fields - always show in filter if viewing all stores or store has data
        const paymentFields = [];
        // Vouchers - always available as filter option
        paymentFields.push({ key: 'vouchers', label: 'Vouchers' });
        // AMEX - always available as filter option
        paymentFields.push({ key: 'amex', label: 'AMEX' });
        if (hasStaffExpenses()) {
          paymentFields.push({ key: 'staff_expenses', label: 'Staff Expenses' });
        }
        
        // Individual fuel card columns (only for fuel stations)
        const fuelCardFields = [];
        if (isFuelStation()) {
          const activeFuelCards = getActiveFuelCards();
          activeFuelCards.forEach(fc => {
            fuelCardFields.push({ key: `fuel_card_${fc.id}`, label: fc.name });
          });
        }
        
        // Conditional fields based on store configuration
        const conditionalFields = [];
        
        // Fuel-related fields (only for fuel stations)
        if (isFuelStation()) {
          conditionalFields.push({ key: 'adblue_sales', label: 'Ad Blue Sales' });
          conditionalFields.push({ key: 'drive_off', label: 'Drive Off' });
          conditionalFields.push({ key: 'indebtedness', label: 'Indebtedness' });
          conditionalFields.push({ key: 'recovered_debt', label: 'Recovered Debt' });
        }
        
        // Coffee tracking
        if (isCoffeeEnabled()) {
          conditionalFields.push({ key: 'coffee_sales', label: 'Coffee Sales' });
        }
        
        // Lottery services
        if (isServiceEnabled('lottery')) {
          conditionalFields.push({ key: 'lottery_sales', label: 'Lottery Sales' });
          conditionalFields.push({ key: 'lottery_prize', label: 'Lottery Prize' });
        }
        
        // Scratch cards
        if (isServiceEnabled('scratch_cards')) {
          conditionalFields.push({ key: 'scratch_card_sales', label: 'Scratch Card Sales' });
          conditionalFields.push({ key: 'scratch_prize', label: 'Scratch Prize' });
        }
        
        // Paypoint service
        if (isServiceEnabled('paypoint')) {
          conditionalFields.push({ key: 'paypoint', label: 'Paypoint' });
        }
        
        // Air machine
        if (isServiceEnabled('air_machine')) {
          conditionalFields.push({ key: 'air_machine', label: 'Air Machine' });
        }
        
        // End fields
        const endFields = [
          { key: 'paid_out', label: 'Paid Out' },
          { key: 'variance', label: 'Variance' },
          { key: 'submitted_by', label: 'Submitted By' }
        ];
        
        // Combine: standard + payments + fuel cards + conditional + end
        return [...standardFields, ...paymentFields, ...fuelCardFields, ...conditionalFields, ...endFields];
        
      case 'variance':
        return [
          { key: 'store', label: 'Store' },
          { key: 'date', label: 'Date' },
          { key: 'shift', label: 'Shift' },
          { key: 'variance', label: 'Variance' },
          { key: 'reason', label: 'Reason' }
        ];
      case 'lending':
        return [
          { key: 'transaction_id', label: 'Transaction ID' },
          { key: 'date', label: 'Date' },
          { key: 'name', label: 'Creditor Name' },
          { key: 'description', label: 'Description' },
          { key: 'source', label: 'Source' },
          { key: 'entered_by', label: 'Entered By' }
        ];
      case 'investments':
        return [
          { key: 'transaction_id', label: 'Transaction ID' },
          { key: 'date', label: 'Date' },
          { key: 'name', label: 'Scheme Name' },
          { key: 'direction', label: 'Direction' },
          { key: 'amount', label: 'Amount' },
          { key: 'source', label: 'Source' },
          { key: 'description', label: 'Description' },
          { key: 'entered_by', label: 'Entered By' }
        ];
      case 'cashcontrol':
        return [
          { key: 'transaction_id', label: 'Transaction ID' },
          { key: 'date', label: 'Date' },
          { key: 'transfer_type', label: 'Type' },
          { key: 'amount', label: 'Amount' },
          { key: 'source', label: 'Source' },
          { key: 'description', label: 'Description' },
          { key: 'reference', label: 'Reference' },
          { key: 'entered_by', label: 'Entered By' }
        ];
      case 'purchases':
        return [
          { key: 'date', label: 'Date' },
          { key: 'supplier', label: 'Supplier' },
          { key: 'invoice_number', label: 'Invoice #' },
          { key: 'net_amount', label: 'Net Amount' },
          { key: 'vat_amount', label: 'VAT Amount' },
          { key: 'total_amount', label: 'Total Amount' },
          { key: 'payment_type', label: 'Payment Type' },
          { key: 'entered_by', label: 'Entered By' }
        ];
      case 'scratch_settlements':
        return [
          { key: 'transaction_id', label: 'Transaction ID' },
          { key: 'date', label: 'Date' },
          { key: 'total_cards', label: 'Total Cards' },
          { key: 'total_value', label: 'Total Value' },
          { key: 'entered_by', label: 'Entered By' }
        ];
      case 'scratch_sales':
        return [
          { key: 'date', label: 'Date' },
          { key: 'shift', label: 'Shift' },
          { key: 'scratch_sales', label: 'Sales Amount' },
          { key: 'scratch_prize_paid', label: 'Prize Paid' },
          { key: 'submitted_by', label: 'Submitted By' }
        ];
      case 'fuel_deliveries':
        return [
          { key: 'date', label: 'Date' },
          { key: 'grade', label: 'Grade' },
          { key: 'quantity', label: 'Quantity (L)' },
          { key: 'cost_per_litre', label: 'Cost/L' },
          { key: 'total_cost', label: 'Total Cost' },
          { key: 'invoice_number', label: 'Invoice #' },
          { key: 'entered_by', label: 'Entered By' }
        ];
      case 'fuel_sales':
        return [
          { key: 'date', label: 'Date' },
          { key: 'shift', label: 'Shift' },
          { key: 'grade', label: 'Grade' },
          { key: 'total_volume', label: 'Total Volume (L)' },
          { key: 'price_per_litre', label: 'Price/L' },
          { key: 'total_sales', label: 'Total Sales' },
          { key: 'submitted_by', label: 'Submitted By' }
        ];
      case 'fuel_prices':
        return [
          { key: 'date', label: 'Date' },
          { key: 'store_name', label: 'Store' },
          { key: 'grade_name', label: 'Grade' },
          { key: 'cost_price', label: 'Cost Price' },
          { key: 'selling_price', label: 'Selling Price' },
          { key: 'margin', label: 'Margin' }
        ];
      case 'store':
        return [
          { key: 'store_name', label: 'Store' },
          { key: 'total_sales', label: 'Total Sales' },
          { key: 'total_cash', label: 'Cash' },
          { key: 'total_card', label: 'Card' },
          { key: 'total_variance', label: 'Variance' }
        ];
      default:
        return [];
    }
  };

  // Filter data based on selected fields
  const filterDataByFields = (data, selectedFieldKeys) => {
    if (!data || data.length === 0) return data;
    if (!selectedFieldKeys || selectedFieldKeys.length === 0) return data;
    
    return data.map(row => {
      const filtered = {};
      selectedFieldKeys.forEach(key => {
        if (row.hasOwnProperty(key)) {
          filtered[key] = row[key];
        }
      });
      return filtered;
    });
  };

  // Check if a field should be visible based on selectedFields
  const isFieldVisible = (fieldKey) => {
    // If no filters have been set, show all fields (default behavior)
    const hasFilters = Object.keys(selectedFields).length > 0;
    if (!hasFilters) return true;
    // If the field is explicitly set to false, hide it
    return selectedFields[fieldKey] !== false;
  };

  // Filter fuel delivery data by selected grade
  const filteredFuelDeliveryReport = selectedFuelGrade === 'all' 
    ? fuelDeliveryReport 
    : fuelDeliveryReport.filter(d => d.grade_name === selectedFuelGrade);

  // Filter and aggregate fuel sales data based on selected grade
  const filteredFuelSalesReport = (() => {
    if (selectedFuelGrade === 'all') {
      // Aggregate by date+shift when "All Grades" selected
      const aggregated = new Map();
      fuelSalesReport.forEach(s => {
        const key = `${s.date}-${s.shift}`;
        if (aggregated.has(key)) {
          const existing = aggregated.get(key);
          existing.total_volume += s.total_volume || 0;
          existing.total_sales += s.total_sales || 0;
        } else {
          aggregated.set(key, {
            date: s.date,
            shift: s.shift,
            grade_name: 'All Grades',
            total_volume: s.total_volume || 0,
            total_sales: s.total_sales || 0,
            submitted_by: s.submitted_by
          });
        }
      });
      return Array.from(aggregated.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
    } else {
      // Show individual grade entries when specific grade selected
      return fuelSalesReport.filter(s => s.grade_name === selectedFuelGrade);
    }
  })();

  // Filter creditor report by selected creditor, transaction type, and from last payment
  const filteredCreditorReport = (() => {
    let filtered = creditorReport;
    
    // Filter by creditor
    if (selectedCreditor !== 'all') {
      filtered = filtered.filter(t => t.creditor_id === selectedCreditor || t.entity_id === selectedCreditor);
    }
    
    // Filter by transaction type
    if (creditorTransactionType === 'credit') {
      filtered = filtered.filter(t => t.direction === 'out');
    } else if (creditorTransactionType === 'repayment') {
      filtered = filtered.filter(t => t.direction === 'in');
    }
    
    // Filter from last payment (only when a specific creditor is selected)
    if (showFromLastPayment && selectedCreditor !== 'all') {
      // Find the last repayment date for this creditor
      const repayments = creditorReport
        .filter(t => (t.creditor_id === selectedCreditor || t.entity_id === selectedCreditor) && t.direction === 'in')
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      if (repayments.length > 0) {
        const lastPaymentDate = new Date(repayments[0].date);
        filtered = filtered.filter(t => new Date(t.date) >= lastPaymentDate);
      }
    }
    
    return filtered;
  })();

  // Calculate total outstanding for selected creditor (all time)
  const getCreditorOutstanding = () => {
    let relevantTransactions = creditorReport;
    if (selectedCreditor !== 'all') {
      relevantTransactions = creditorReport.filter(t => t.creditor_id === selectedCreditor || t.entity_id === selectedCreditor);
    }
    const totalLent = relevantTransactions.filter(t => t.direction === 'out').reduce((a, t) => a + (t.amount || 0), 0);
    const totalRepaid = relevantTransactions.filter(t => t.direction === 'in').reduce((a, t) => a + (t.amount || 0), 0);
    return totalLent - totalRepaid;
  };

  // Filter investment report by selected scheme
  const filteredInvestmentReport = selectedScheme === 'all'
    ? investmentReport
    : investmentReport.filter(t => t.scheme_id === selectedScheme || t.entity_id === selectedScheme);

  // Calculate fuel card total for an entry
  const calculateFuelCardTotal = (entry) => {
    let fuelCardTotal = 0;
    const fuelCardTransactions = entry.fuel_card_transactions || {};
    
    // Non-bunkering cards (BP, AllStar, etc.)
    const nonBunkering = fuelCardTransactions.non_bunkering || {};
    Object.values(nonBunkering).forEach(amount => {
      fuelCardTotal += parseFloat(amount) || 0;
    });
    
    // Bunkering cards
    const bunkering = fuelCardTransactions.bunkering || {};
    Object.values(bunkering).forEach(cardData => {
      if (typeof cardData === 'object' && cardData !== null) {
        fuelCardTotal += parseFloat(cardData.total_amount) || 0;
      } else {
        fuelCardTotal += parseFloat(cardData) || 0;
      }
    });
    
    // Key fuels
    const keyFuels = fuelCardTransactions.key_fuels || {};
    if (typeof keyFuels === 'object' && keyFuels !== null) {
      fuelCardTotal += parseFloat(keyFuels.total_amount) || 0;
    }
    
    return fuelCardTotal;
  };

  // Calculate fuel sales for an entry
  const calculateFuelSales = (entry) => {
    if (!entry.fuel_volumes || !entry.fuel_prices) return 0;
    let total = 0;
    Object.keys(entry.fuel_volumes || {}).forEach(gradeId => {
      const volume = parseFloat(entry.fuel_volumes[gradeId]) || 0;
      const price = parseFloat(entry.fuel_prices[gradeId]) || 0; // price is in pennies
      total += volume * price;
    });
    return total / 100; // Convert from pennies to pounds
  };

  // Get department amount by name from entry
  const getDepartmentAmount = (entry, deptName) => {
    const store = stores.find(s => s.id === entry.store_id);
    if (!store || !entry.department_amounts) return 0;
    
    const dept = (store.departments || []).find(d => 
      d.name?.toLowerCase().includes(deptName.toLowerCase())
    );
    if (!dept) return 0;
    
    return parseFloat(entry.department_amounts[dept.id]) || 0;
  };

  // Get service amount by key from entry
  const getServiceAmount = (entry, serviceKey) => {
    if (!entry.service_amounts) return 0;
    return parseFloat(entry.service_amounts[serviceKey]) || 0;
  };

  // Helper function to get cash amount from entry (checks legacy field first, then payment_amounts)
  const getCashAmount = (entry) => {
    // If legacy field exists (even if 0), trust it - user may have explicitly set it to 0
    if (entry.cash_amount !== undefined && entry.cash_amount !== null) {
      return parseFloat(entry.cash_amount) || 0;
    }
    // Only use payment_amounts as fallback when legacy field is completely absent
    const paymentAmounts = entry.payment_amounts || {};
    return parseFloat(paymentAmounts['cash'] || paymentAmounts['Cash'] || 0) || 0;
  };

  // Helper function to get card amount from entry (checks legacy field first, then payment_amounts)
  const getCardAmount = (entry) => {
    // If legacy field exists (even if 0), trust it - user may have explicitly set it to 0
    if (entry.credit_card_amount !== undefined && entry.credit_card_amount !== null) {
      return parseFloat(entry.credit_card_amount) || 0;
    }
    // Only use payment_amounts as fallback when legacy field is completely absent
    const paymentAmounts = entry.payment_amounts || {};
    return parseFloat(paymentAmounts['card'] || paymentAmounts['Card Payments'] || paymentAmounts['Card Payments (BMS)'] || 0) || 0;
  };

  // Helper function to get AMEX amount from entry
  const getAmexAmount = (entry) => {
    // If legacy field exists (even if 0), trust it
    if (entry.amex_amount !== undefined && entry.amex_amount !== null) {
      return parseFloat(entry.amex_amount) || 0;
    }
    // Only use payment_amounts as fallback when legacy field is completely absent
    const paymentAmounts = entry.payment_amounts || {};
    return parseFloat(paymentAmounts['amex'] || paymentAmounts['AMEX'] || paymentAmounts['Amex'] || 0) || 0;
  };

  // Helper function to get vouchers amount from entry (checks legacy field first, then payment_amounts)
  const getVouchersAmount = (entry) => {
    // If legacy field exists (even if 0), trust it - user may have explicitly set it to 0
    if (entry.vouchers_amount !== undefined && entry.vouchers_amount !== null) {
      return parseFloat(entry.vouchers_amount) || 0;
    }
    // Only use payment_amounts as fallback when legacy field is completely absent
    const paymentAmounts = entry.payment_amounts || {};
    return parseFloat(paymentAmounts['vouchers'] || paymentAmounts['Voucher'] || paymentAmounts['Vouchers'] || 0) || 0;
  };

  // Calculate total tender (all payments received) for an entry - INTERNAL USE ONLY for variance
  // MUST MATCH SalesEntryPage: totalPayments = cashPayment + voucherPayment + cardBmsPayment + amexPayment + fuelCardTotals.total
  // NOTE: Staff Expenses is NOT included in tender/totalPayments for variance calculation
  const calculateTender = (entry, store) => {
    const paymentAmounts = entry.payment_amounts || {};
    
    // Cash - always included
    const cash = parseFloat(paymentAmounts.cash || paymentAmounts.Cash || 0) || 0;
    
    // Card Payments (BMS) - always included
    const card = parseFloat(paymentAmounts.card || paymentAmounts['Card Payments'] || paymentAmounts['Card Payments (BMS)'] || 0) || 0;
    
    // Vouchers - check all possible field names (no store check - count if data exists)
    const vouchers = parseFloat(
      paymentAmounts.vouchers || 
      paymentAmounts.Voucher || 
      paymentAmounts.Vouchers || 
      paymentAmounts.voucher ||
      entry.vouchers_amount ||  // Legacy field
      0
    ) || 0;
    
    // AMEX - check all possible field names (no store check - count if data exists)
    const amex = parseFloat(
      paymentAmounts.amex || 
      paymentAmounts.AMEX || 
      paymentAmounts.Amex ||
      0
    ) || 0;
    
    // Fuel Card transactions (for fuel stations only)
    let fuelCardTotal = 0;
    if (store?.store_type === 'fuel_station') {
      const fuelCardTransactions = entry.fuel_card_transactions || {};
      
      // Non-bunkering cards
      const nonBunkering = fuelCardTransactions.non_bunkering || {};
      Object.values(nonBunkering).forEach(amount => {
        fuelCardTotal += parseFloat(amount) || 0;
      });
      
      // Bunkering cards
      const bunkering = fuelCardTransactions.bunkering || {};
      Object.values(bunkering).forEach(cardData => {
        if (typeof cardData === 'object' && cardData !== null) {
          fuelCardTotal += parseFloat(cardData.total_amount) || 0;
        } else {
          fuelCardTotal += parseFloat(cardData) || 0;
        }
      });
      
      // Key fuels
      const keyFuels = fuelCardTransactions.key_fuels || {};
      if (typeof keyFuels === 'object' && keyFuels !== null) {
        fuelCardTotal += parseFloat(keyFuels.total_amount) || 0;
      }
    }
    
    // Staff Expenses is NOT included in tender (it's tracked separately, not part of variance calculation)
    return cash + card + vouchers + amex + fuelCardTotal;
  };

  // Calculate expected amount for an entry
  // MUST MATCH SalesEntryPage variance calculation EXACTLY
  const calculateExpected = (entry, store) => {
    const isFuelStation = store?.store_type === 'fuel_station';
    
    // Total Sales (Z-Reading / Shop) + Service Sales
    // SalesEntryPage: totalSales = (enteredTotalSales OR departmentSales) + totalServiceSales
    const baseSales = parseFloat(entry.total_sales_amount) || 0;
    
    // Service amounts (PayPoint, Coffee, Air Machine, etc.) - MUST be included like SalesEntryPage
    const serviceAmounts = entry.service_amounts || entry.service_sales || {};
    const totalServiceSales = Object.values(serviceAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    
    const totalSales = baseSales + totalServiceSales;
    
    // Fuel Sales Z-Reading (for fuel stations only)
    const fuelSalesZReading = isFuelStation ? (parseFloat(entry.fuel_sales_dept_amount) || 0) : 0;
    
    // Prizes to deduct
    const lotteryPrize = parseFloat(entry.lottery_prize_paid) || 0;
    const scratchPrize = parseFloat(entry.scratch_card_prize_paid) || 0;
    
    // Paid Outs - stored as NEGATIVE value in database
    // Use total_paid_out directly (negative) rather than summing paid_out_items (which may be positive)
    const totalPaidOut = parseFloat(entry.total_paid_out) || 0;
    
    // Incident deductions - from driveoff_dept_amount (stored when entry was saved)
    const incidentDeductions = isFuelStation ? (parseFloat(entry.driveoff_dept_amount) || 0) : 0;
    
    // Incident recoveries - use calculated value from backend (matches SalesEntryPage logic)
    // Backend calculates this from incidents where resolution_date === sales_date and status is paid_store/paid_agency
    const incidentRecoveries = isFuelStation 
      ? (parseFloat(entry.calculated_incident_recoveries) || parseFloat(entry.incident_recoveries) || 0) 
      : 0;
    
    // Expected = Sales + FuelSalesZReading - Prizes - PaidOuts - Incidents + Recoveries
    return totalSales + fuelSalesZReading - lotteryPrize - scratchPrize - totalPaidOut - incidentDeductions + incidentRecoveries;
  };

  // Calculate variance for an entry: Tender - Expected
  // ALWAYS recalculate variance using same formula as SalesEntryPage
  // Do NOT use stored frontend_variance as it may be outdated or corrupted
  const calculateVariance = (entry, store) => {
    const tender = calculateTender(entry, store);
    const expected = calculateExpected(entry, store);
    return tender - expected;
  };
  
  // Helper to get individual fuel card amount from entry
  const getFuelCardAmount = (entry, cardTypeId) => {
    if (!entry.fuel_card_transactions) return 0;
    const { non_bunkering = {}, bunkering = {}, key_fuels = {} } = entry.fuel_card_transactions;
    
    // Check non-bunkering
    if (non_bunkering[cardTypeId]) {
      return parseFloat(non_bunkering[cardTypeId]) || 0;
    }
    
    // Check bunkering
    if (bunkering[cardTypeId]) {
      const cardData = bunkering[cardTypeId];
      if (typeof cardData === 'object' && cardData !== null) {
        return parseFloat(cardData.total_amount) || 0;
      }
      return parseFloat(cardData) || 0;
    }
    
    // Check key_fuels (special case)
    if (cardTypeId === 'key_fuels' && key_fuels) {
      return parseFloat(key_fuels.total_amount) || 0;
    }
    
    return 0;
  };
  
  // Get staff expenses amount from entry
  const getStaffExpensesAmount = (entry) => {
    const paymentAmounts = entry.payment_amounts || {};
    return parseFloat(paymentAmounts['Staff Expenses'] || paymentAmounts.staff_expenses || 0) || 0;
  };

  // Check if a column has any non-zero values across all daily summary entries
  const hasNonZeroValues = (entries, getValue) => {
    if (!entries || entries.length === 0) return false;
    return entries.some(entry => {
      const value = getValue(entry);
      return value !== 0 && value !== null && value !== undefined;
    });
  };

  // Get selected store name for reports
  const getSelectedStoreName = () => {
    if (selectedStoreId === 'all') return 'All Stores';
    const store = stores.find(s => s.id === selectedStoreId);
    return store?.name || 'Unknown Store';
  };

  // Generate share content for different report types
  const generateShareContent = (type) => {
    const storeName = getSelectedStoreName();
    const dateRangeStr = `${format(dateRange.start, 'dd/MM/yyyy')} - ${format(dateRange.end, 'dd/MM/yyyy')}`;
    
    switch (type) {
      case 'daily':
        if (!dailySummary) return { title: 'Daily Summary Report', content: 'No data available' };
        return {
          title: 'Daily Summary Report',
          content: `📍 Store: ${storeName}
📅 Date: ${format(dateRange.start, 'dd/MM/yyyy')}

💰 Total Sales: ${formatCurrency(dailySummary.summary?.total_sales || 0)}
💵 Cash: ${formatCurrency(dailySummary.summary?.total_cash || 0)}
💳 Card: ${formatCurrency(dailySummary.summary?.total_card || 0)}
⚠️ Variance: ${formatCurrency(dailySummary.summary?.total_variance || 0)}
📝 Entries: ${dailySummary.entries?.length || 0}`
        };
      
      case 'store':
        return {
          title: 'Store Summary Report',
          content: `📍 Stores: ${storeName}
📅 Period: ${dateRangeStr}

${storeSummary.map(s => `• ${s.store_name}: ${formatCurrency(s.total_sales)}`).join('\n')}`
        };
      
      case 'variance':
        const totalVariance = varianceReport.reduce((a, v) => a + (v.variance || 0), 0);
        return {
          title: 'Variance Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

⚠️ Total Variance: ${formatCurrency(totalVariance)}
📝 Entries with Variance: ${varianceReport.length}`
        };
      
      case 'lending':
        const totalLent = creditorReport.filter(t => t.direction === 'out').reduce((a, t) => a + t.amount, 0);
        const totalReturned = creditorReport.filter(t => t.direction === 'in').reduce((a, t) => a + t.amount, 0);
        return {
          title: 'Creditor Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

💸 Total Lent: ${formatCurrency(totalLent)}
💰 Total Returned: ${formatCurrency(totalReturned)}
📊 Outstanding: ${formatCurrency(totalLent - totalReturned)}
📝 Transactions: ${creditorReport.length}`
        };
      
      case 'investment':
        const totalInvested = investmentReport.filter(t => t.direction === 'out').reduce((a, t) => a + t.amount, 0);
        const totalInvestReturned = investmentReport.filter(t => t.direction === 'in').reduce((a, t) => a + t.amount, 0);
        return {
          title: 'Investments Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

💼 Total Invested: ${formatCurrency(totalInvested)}
💰 Total Returned: ${formatCurrency(totalInvestReturned)}
📊 Outstanding: ${formatCurrency(totalInvested - totalInvestReturned)}
📝 Transactions: ${investmentReport.length}`
        };
      
      case 'cashcontrol':
        const totalCashControl = cashControlReport.reduce((a, t) => a + t.amount, 0);
        return {
          title: 'Cash Control Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

💰 Total Amount: ${formatCurrency(totalCashControl)}
📝 Transactions: ${cashControlReport.length}`
        };
      
      case 'purchases':
        const totalPurchases = purchaseReport.reduce((a, p) => a + (p.total_amount || 0), 0);
        return {
          title: 'Purchases Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

🛒 Total Purchases: ${formatCurrency(totalPurchases)}
📝 Invoices: ${purchaseReport.length}`
        };
      
      case 'scratchcard_settled':
        const totalCards = scratchSettlementReport.reduce((a, s) => a + (s.total_cards || 0), 0);
        const totalValue = scratchSettlementReport.reduce((a, s) => a + (s.total_value || 0), 0);
        return {
          title: 'Scratch Card Settlements Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

🎫 Total Cards Settled: ${totalCards.toLocaleString()}
💰 Total Value: ${formatCurrency(totalValue)}
📝 Settlements: ${scratchSettlementReport.length}`
        };
      
      case 'scratchcard_sales':
        const totalScratchSales = scratchSalesReport.reduce((a, s) => a + (s.scratch_sales || 0), 0);
        const totalPrizePaid = scratchSalesReport.reduce((a, s) => a + (s.scratch_prize_paid || 0), 0);
        return {
          title: 'Scratch Card Sales Report',
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}

🎫 Total Sales: ${formatCurrency(totalScratchSales)}
🏆 Prize Paid: ${formatCurrency(totalPrizePaid)}
📊 Net Sales: ${formatCurrency(totalScratchSales - totalPrizePaid)}`
        };
      
      case 'fuel_deliveries':
        const totalDeliveryQty = filteredFuelDeliveryReport.reduce((a, d) => a + (d.quantity || 0), 0);
        const totalDeliveryCost = filteredFuelDeliveryReport.reduce((a, d) => a + (d.total_cost || 0), 0);
        return {
          title: `Fuel Deliveries Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`,
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}
${selectedFuelGrade !== 'all' ? `⛽ Grade: ${selectedFuelGrade}\n` : ''}
🛢️ Total Quantity: ${totalDeliveryQty.toLocaleString()} L
💰 Total Cost: ${formatCurrency(totalDeliveryCost)}
📝 Deliveries: ${filteredFuelDeliveryReport.length}`
        };
      
      case 'fuel_sales':
        const totalFuelVolume = filteredFuelSalesReport.reduce((a, s) => a + (s.total_volume || 0), 0);
        const totalFuelSales = filteredFuelSalesReport.reduce((a, s) => a + (s.total_sales || 0), 0);
        return {
          title: `Fuel Sales Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`,
          content: `📍 Store: ${storeName}
📅 Period: ${dateRangeStr}
${selectedFuelGrade !== 'all' ? `⛽ Grade: ${selectedFuelGrade}\n` : ''}
🛢️ Total Volume: ${totalFuelVolume.toLocaleString()} L
💰 Total Sales: ${formatCurrency(totalFuelSales)}
📝 Entries: ${filteredFuelSalesReport.length}`
        };
      
      case 'compare':
        if (!compareData) return { title: 'Period Comparison Report', content: 'No data available' };
        return {
          title: 'Period Comparison Report',
          content: `📍 Store: ${storeName}

📅 Period 1: ${format(new Date(compareData.period1.start), 'dd/MM/yyyy')} - ${format(new Date(compareData.period1.end), 'dd/MM/yyyy')}
💰 Sales: ${formatCurrency(compareData.period1.total_sales)}

📅 Period 2: ${format(new Date(compareData.period2.start), 'dd/MM/yyyy')} - ${format(new Date(compareData.period2.end), 'dd/MM/yyyy')}
💰 Sales: ${formatCurrency(compareData.period2.total_sales)}

📊 Change: ${compareData.comparison.sales_percentage_change}%`
        };
      
      default:
        return { title: 'Report', content: 'Report data' };
    }
  };

  // Send report via email with PDF attachment (same PDF as download)
  const sendReportEmail = async () => {
    if (!emailRecipient || !emailRecipient.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    try {
      const { title, content } = generateShareContent(reportType);
      const storeName = getSelectedStoreName();
      
      // Get the same export data that would be used for PDF download
      let exportData = [];
      let reportTitle = title;
      
      switch (reportType) {
        case 'daily':
          if (dailySummary?.entries) {
            exportData = dailySummary.entries.map(entry => {
              const store = stores.find(s => s.id === entry.store_id);
              return {
                store: store?.name || 'Unknown',
                date: entry.sales_date,
                shift: entry.shift?.toUpperCase(),
                total_sales: entry.total_sales_amount,
                cash: getCashAmount(entry),
                card: getCardAmount(entry),
                lottery: entry.lottery_sales || 0,
                scratch: entry.service_amounts?.scratch_cards || 0,
                fuel_sales: calculateFuelSales(entry),
                paid_out: entry.total_paid_out || 0,
                variance: entry.variance,
                submitted_by: getUserName(entry.submitted_by)
              };
            });
          }
          reportTitle = 'Daily Summary Report';
          break;
        case 'store':
          exportData = storeSummary.map(s => ({
            store: s.store_name,
            total_sales: s.total_sales,
            cash: s.total_cash,
            card: s.total_card,
            paid_out: s.total_paid_out,
            variance: s.total_variance,
            entries: s.entry_count
          }));
          reportTitle = 'Store Summary Report';
          break;
        case 'variance':
          exportData = varianceReport.map(e => ({
            date: new Date(e.sales_date).toLocaleDateString('en-GB'),
            store: e.store_name,
            shift: e.shift?.toUpperCase(),
            variance: e.variance,
            reason: e.variance_reason || '-'
          }));
          reportTitle = 'Variance Report';
          break;
        case 'lending':
          exportData = filteredCreditorReport.map(t => ({
            id: t.transaction_id || '-',
            date: t.date,
            creditor_name: t.entity_name || '-',
            description: t.description || '-',
            credit: t.direction === 'out' ? t.amount : 0,
            repayment: t.direction === 'in' ? t.amount : 0,
            source: (t.source || 'cash').replace('_', ' '),
            entered_by: t.created_by_name || '-'
          }));
          const creditorName = selectedCreditor !== 'all' ? creditors.find(c => c.id === selectedCreditor)?.name : '';
          reportTitle = `Creditor Report${creditorName ? ` - ${creditorName}` : ''}`;
          if (showFromLastPayment && selectedCreditor !== 'all') {
            reportTitle += ' (From Last Payment)';
          }
          break;
        case 'investments':
          exportData = filteredInvestmentReport.map(t => ({
            id: t.transaction_id || '-',
            date: t.date,
            scheme: t.entity_name || t.scheme_name || '-',
            direction: t.direction === 'out' ? 'Invested' : 'Returned',
            amount: t.amount,
            source: (t.source || 'cash').replace('_', ' '),
            entered_by: t.created_by_name || '-'
          }));
          const schemeName = selectedScheme !== 'all' ? schemes.find(s => s.id === selectedScheme)?.name : '';
          reportTitle = `Investments Report${schemeName ? ` - ${schemeName}` : ''}`;
          break;
        case 'cashcontrol':
          exportData = cashControlReport.map(t => ({
            id: t.transaction_id || '-',
            date: t.date,
            type: t.transfer_type,
            amount: t.amount,
            source: t.source || 'cash',
            description: t.description || '-',
            reference: t.reference || '-',
            entered_by: getUserName(t.user_id)
          }));
          reportTitle = 'Cash Control Report';
          break;
        case 'purchases':
          exportData = purchaseReport.map(p => ({
            date: p.date,
            supplier: p.supplier_name || '-',
            invoice: p.invoice_number || '-',
            net_amount: p.net_amount,
            vat_amount: p.vat_amount,
            total_amount: p.total_amount,
            payment: p.payment_type || '-',
            entered_by: getUserName(p.user_id)
          }));
          reportTitle = 'Purchases Report';
          break;
        case 'scratchcard_settled':
          exportData = scratchSettlementReport.map(s => ({
            id: s.transaction_id || '-',
            date: s.date,
            total_cards: s.total_cards,
            total_value: s.total_value,
            entered_by: getUserName(s.user_id)
          }));
          reportTitle = 'Scratch Card Settlements Report';
          break;
        case 'scratchcard_sales':
          exportData = scratchSalesReport.map(s => ({
            date: s.date,
            shift: s.shift?.toUpperCase(),
            sales: s.scratch_sales,
            prize_paid: s.scratch_prize_paid,
            net_sales: s.scratch_sales - s.scratch_prize_paid,
            submitted_by: getUserName(s.submitted_by)
          }));
          reportTitle = 'Scratch Card Sales Report';
          break;
        case 'fuel_deliveries':
          exportData = filteredFuelDeliveryReport.map(d => ({
            date: d.date,
            grade: d.grade_name || '-',
            quantity: d.quantity,
            cost_per_litre: `${d.cost_per_litre?.toFixed(2)}p`,
            total_cost: d.total_cost,
            invoice: d.invoice_number || '-',
            entered_by: getUserName(d.user_id)
          }));
          reportTitle = `Fuel Deliveries Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`;
          break;
        case 'fuel_sales':
          exportData = filteredFuelSalesReport.map(s => {
            const row = {
              date: s.date,
              shift: s.shift?.toUpperCase(),
              total_volume: s.total_volume,
              total_sales: s.total_sales,
              submitted_by: getUserName(s.submitted_by)
            };
            if (selectedFuelGrade !== 'all') {
              row.grade = s.grade_name || '-';
              row.price_per_litre = s.price_per_litre;
            }
            return row;
          });
          reportTitle = `Fuel Sales Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`;
          break;
        default:
          toast.error('This report type cannot be emailed');
          setSendingEmail(false);
          return;
      }

      if (exportData.length === 0) {
        toast.error('No data available to send');
        setSendingEmail(false);
        return;
      }

      // Generate the same PDF as download
      const doc = generatePDFDocument(exportData, reportTitle, true);
      
      // Add Outstanding Balance section for Creditor Report
      if (reportType === 'lending') {
        const outstanding = getCreditorOutstanding();
        const finalY = doc.lastAutoTable?.finalY || 150;
        const creditorName = selectedCreditor !== 'all' ? creditors.find(c => c.id === selectedCreditor)?.name : '';
        
        // Draw outstanding balance box - Dark blue background
        doc.setFillColor(30, 58, 138);
        doc.rect(14, finalY + 10, doc.internal.pageSize.width - 28, 25, 'F');
        doc.setDrawColor(59, 130, 246);
        doc.rect(14, finalY + 10, doc.internal.pageSize.width - 28, 25, 'S');
        
        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        doc.text('TOTAL OUTSTANDING BALANCE', 20, finalY + 22);
        
        doc.setFontSize(12);
        doc.setTextColor(200, 200, 200);
        const balanceLabel = selectedCreditor !== 'all' 
          ? `All-time balance for ${creditorName || 'this creditor'}`
          : 'Combined outstanding balance for all creditors';
        doc.text(balanceLabel, 20, finalY + 30);
        
        doc.setFontSize(16);
        if (outstanding > 0) {
          doc.setTextColor(251, 191, 36);
        } else {
          doc.setTextColor(74, 222, 128);
        }
        doc.text(formatCurrency(outstanding), doc.internal.pageSize.width - 20, finalY + 25, { align: 'right' });
      }
      
      const pdfBase64 = doc.output('datauristring');
      
      // Send email via backend
      const response = await axios.post(`${API}/reports/send-email`, {
        to_email: emailRecipient,
        subject: `${reportTitle} - ${storeName}`,
        report_content: content.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]/gu, ''),
        pdf_data: pdfBase64,
        pdf_filename: `${reportTitle.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`
      });
      
      if (response.data.status === 'sent') {
        toast.success(`Report sent to ${emailRecipient}`);
        setShowEmailDialog(false);
        setEmailRecipient('');
      } else if (response.data.status === 'not_configured') {
        toast.error('Email not configured. Please set up SMTP in System Settings.');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Share report via WhatsApp with PDF download link
  const shareReportViaWhatsApp = async () => {
    setSharingWhatsApp(true);
    try {
      const { title, content } = generateShareContent(reportType);
      const storeName = getSelectedStoreName();
      
      // Get the same export data
      let exportData = [];
      let reportTitle = title;
      
      switch (reportType) {
        case 'daily':
          if (dailySummary?.entries) {
            exportData = dailySummary.entries.map(entry => {
              const store = stores.find(s => s.id === entry.store_id);
              return {
                store: store?.name || 'Unknown',
                date: entry.sales_date,
                shift: entry.shift?.toUpperCase(),
                total_sales: entry.total_sales_amount,
                cash: getCashAmount(entry),
                card: getCardAmount(entry),
                variance: entry.variance
              };
            });
          }
          reportTitle = 'Daily Summary Report';
          break;
        case 'store':
          exportData = storeSummary.map(s => ({
            store: s.store_name,
            total_sales: s.total_sales,
            cash: s.total_cash,
            card: s.total_card,
            variance: s.total_variance
          }));
          reportTitle = 'Store Summary Report';
          break;
        case 'variance':
          exportData = varianceReport.map(e => ({
            date: new Date(e.sales_date).toLocaleDateString('en-GB'),
            store: e.store_name,
            shift: e.shift?.toUpperCase(),
            variance: e.variance,
            reason: e.variance_reason || '-'
          }));
          reportTitle = 'Variance Report';
          break;
        case 'lending':
          exportData = filteredCreditorReport.map(t => ({
            date: t.date,
            creditor: t.entity_name || '-',
            credit: t.direction === 'out' ? t.amount : 0,
            repayment: t.direction === 'in' ? t.amount : 0
          }));
          const whatsappCreditorName = selectedCreditor !== 'all' ? creditors.find(c => c.id === selectedCreditor)?.name : '';
          reportTitle = `Creditor Report${whatsappCreditorName ? ` - ${whatsappCreditorName}` : ''}`;
          if (showFromLastPayment && selectedCreditor !== 'all') {
            reportTitle += ' (From Last Payment)';
          }
          break;
        case 'investments':
          exportData = filteredInvestmentReport.map(t => ({
            date: t.date,
            scheme: t.entity_name || t.scheme_name || '-',
            direction: t.direction === 'out' ? 'Invested' : 'Returned',
            amount: t.amount
          }));
          const whatsappSchemeName = selectedScheme !== 'all' ? schemes.find(s => s.id === selectedScheme)?.name : '';
          reportTitle = `Investments Report${whatsappSchemeName ? ` - ${whatsappSchemeName}` : ''}`;
          break;
        case 'cashcontrol':
          exportData = cashControlReport.map(t => ({
            date: t.date,
            type: t.transfer_type,
            amount: t.amount,
            description: t.description || '-'
          }));
          reportTitle = 'Cash Control Report';
          break;
        case 'purchases':
          exportData = purchaseReport.map(p => ({
            date: p.date,
            supplier: p.supplier_name || '-',
            total_amount: p.total_amount
          }));
          reportTitle = 'Purchases Report';
          break;
        case 'scratchcard_settled':
          exportData = scratchSettlementReport.map(s => ({
            date: s.date,
            total_cards: s.total_cards,
            total_value: s.total_value
          }));
          reportTitle = 'Scratch Card Settlements Report';
          break;
        case 'scratchcard_sales':
          exportData = scratchSalesReport.map(s => ({
            date: s.date,
            shift: s.shift?.toUpperCase(),
            sales: s.scratch_sales,
            prize_paid: s.scratch_prize_paid
          }));
          reportTitle = 'Scratch Card Sales Report';
          break;
        case 'fuel_deliveries':
          exportData = filteredFuelDeliveryReport.map(d => ({
            date: d.date,
            grade: d.grade_name || '-',
            quantity: d.quantity,
            cost_per_litre: `${d.cost_per_litre?.toFixed(2)}p`,
            total_cost: d.total_cost
          }));
          reportTitle = `Fuel Deliveries Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`;
          break;
        case 'fuel_sales':
          exportData = filteredFuelSalesReport.map(s => ({
            date: s.date,
            shift: s.shift?.toUpperCase(),
            total_volume: s.total_volume,
            total_sales: s.total_sales
          }));
          reportTitle = `Fuel Sales Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`;
          break;
        default:
          // For unsupported report types, just share text
          shareViaWhatsApp(title, content);
          setSharingWhatsApp(false);
          return;
      }

      if (exportData.length === 0) {
        // No data - just share text summary
        shareViaWhatsApp(title, content);
        setSharingWhatsApp(false);
        return;
      }

      // Get store details
      const store = selectedStoreId === 'all' ? null : stores.find(s => s.id === selectedStoreId);
      
      // Create share link via backend
      const response = await axios.post(`${API}/reports/share`, {
        report_title: reportTitle,
        report_data: exportData,
        store_name: storeName,
        store_details: store ? {
          type: store.store_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          address: store.address
        } : {},
        date_range: {
          start: format(dateRange.start, 'dd/MM/yyyy'),
          end: format(dateRange.end, 'dd/MM/yyyy')
        }
      });

      const downloadUrl = response.data.download_url;
      
      // Create WhatsApp message with download link
      const message = `📊 *${reportTitle}*

📍 Store: ${storeName}
📅 Date: ${format(dateRange.start, 'dd/MM/yyyy')} - ${format(dateRange.end, 'dd/MM/yyyy')}

📥 *Download PDF Report:*
${downloadUrl}

_Link valid for 7 days_
_PROMORTEK Sales Management System_`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Share link created! WhatsApp opened.');
    } catch (error) {
      console.error('WhatsApp share error:', error);
      // Fallback to text-only share
      const { title, content } = generateShareContent(reportType);
      shareViaWhatsApp(title, content);
      toast.info('Sharing text summary (PDF link unavailable)');
    } finally {
      setSharingWhatsApp(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Reports
          </h1>
          <p className="text-muted-foreground mt-1">
            View and export sales reports
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchReports} data-testid="refresh-reports-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select value={selectedStoreId} onValueChange={(val) => {
              setSelectedStoreId(val);
              // Sync with global store context for header display
              if (val !== 'all') {
                const store = stores.find(s => s.id === val);
                if (store) setSelectedStore(store);
              }
            }}>
              <SelectTrigger data-testid="report-store-select">
                <SelectValue placeholder={isSuperUser ? "All Stores" : "Select Store"} />
              </SelectTrigger>
              <SelectContent>
                {isSuperUser && <SelectItem value="all">All Stores</SelectItem>}
                {stores.map(store => (
                  <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily Summary</SelectItem>
                <SelectItem value="store">Store Summary</SelectItem>
                <SelectItem value="variance">Variance Report</SelectItem>
                <SelectItem value="trend">Sales Trend</SelectItem>
                <SelectItem value="compare">Period Comparison</SelectItem>
                <SelectItem value="lending">Creditor Report</SelectItem>
                <SelectItem value="investments">Investments Report</SelectItem>
                <SelectItem value="cashcontrol">Cash Control</SelectItem>
                <SelectItem value="purchases">Purchases</SelectItem>
                <SelectItem value="coffee_tracking">Coffee Tracking</SelectItem>
                <SelectItem value="scratch_settlements">Scratch Card Settlements</SelectItem>
                <SelectItem value="scratch_sales">Scratch Card Sales</SelectItem>
                <SelectItem value="fuel_deliveries">Fuel Deliveries</SelectItem>
                <SelectItem value="fuel_sales">Fuel Sales</SelectItem>
                <SelectItem value="fuel_prices">Fuel Price History</SelectItem>
                <SelectItem value="fuel_profit">Fuel Profit Report</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(dateRange.start, 'PP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateRange.start}
                  onSelect={(date) => date && setDateRange({ ...dateRange, start: date })}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(dateRange.end, 'PP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateRange.end}
                  onSelect={(date) => date && setDateRange({ ...dateRange, end: date })}
                />
              </PopoverContent>
            </Popover>

            <Button 
              variant="outline" 
              onClick={() => setShowFieldFilter(!showFieldFilter)}
              className={showFieldFilter ? 'border-primary' : ''}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter Fields
            </Button>

            {/* Fuel Grade Filter - only show for fuel reports */}
            {(reportType === 'fuel_deliveries' || reportType === 'fuel_sales' || reportType === 'fuel_prices') && availableFuelGrades.length > 0 && (
              <Select value={selectedFuelGrade} onValueChange={setSelectedFuelGrade}>
                <SelectTrigger data-testid="fuel-grade-select" className="w-[180px]">
                  <SelectValue placeholder="All Grades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  {availableFuelGrades.map(grade => (
                    <SelectItem key={grade.name} value={grade.name}>{grade.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Creditor Filter - only show for lending report */}
            {reportType === 'lending' && creditors.length > 0 && (
              <Select value={selectedCreditor} onValueChange={setSelectedCreditor}>
                <SelectTrigger data-testid="creditor-select" className="w-[200px]">
                  <SelectValue placeholder="All Creditors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Creditors</SelectItem>
                  {creditors.filter(c => c.is_active).map(creditor => (
                    <SelectItem key={creditor.id} value={creditor.id}>{creditor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Creditor Transaction Type Filter */}
            {reportType === 'lending' && (
              <Select value={creditorTransactionType} onValueChange={setCreditorTransactionType}>
                <SelectTrigger data-testid="creditor-type-select" className="w-[160px]">
                  <SelectValue placeholder="All Transactions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="credit">Credit Only</SelectItem>
                  <SelectItem value="repayment">Repayment Only</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* From Last Payment Toggle */}
            {reportType === 'lending' && selectedCreditor !== 'all' && (
              <Button
                variant={showFromLastPayment ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFromLastPayment(!showFromLastPayment)}
                data-testid="from-last-payment-btn"
                title="Show transactions since last repayment"
              >
                From Last Payment
              </Button>
            )}

            {/* Scheme Filter - only show for investments report */}
            {reportType === 'investments' && schemes.length > 0 && (
              <Select value={selectedScheme} onValueChange={setSelectedScheme}>
                <SelectTrigger data-testid="scheme-select" className="w-[200px]">
                  <SelectValue placeholder="All Schemes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Schemes</SelectItem>
                  {schemes.filter(s => s.is_active).map(scheme => (
                    <SelectItem key={scheme.id} value={scheme.id}>{scheme.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Share Buttons */}
            <div className="flex gap-1">
              <Button 
                variant="outline" 
                size="icon"
                onClick={shareReportViaWhatsApp}
                disabled={sharingWhatsApp}
                title="Share via WhatsApp"
                data-testid="share-whatsapp-btn"
              >
                {sharingWhatsApp ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Share2 className="h-4 w-4 text-green-500" />
                )}
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setShowEmailDialog(true)}
                title="Send via Email"
                data-testid="share-email-btn"
              >
                <Mail className="h-4 w-4 text-blue-500" />
              </Button>
            </div>
          </div>

          {/* Field Filter Panel */}
          {showFieldFilter && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-3">Select fields to include in report:</p>
              <div className="flex flex-wrap gap-2">
                {getAvailableFields().map(field => (
                  <Button
                    key={field.key}
                    variant={selectedFields[field.key] !== false ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedFields(prev => ({
                      ...prev,
                      [field.key]: prev[field.key] === false ? true : false
                    }))}
                    className="text-xs"
                  >
                    {selectedFields[field.key] !== false && <Check className="h-3 w-3 mr-1" />}
                    {field.label}
                  </Button>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const all = {};
                    getAvailableFields().forEach(f => all[f.key] = true);
                    setSelectedFields(all);
                  }}
                >
                  Select All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedFields({})}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Daily Summary */}
          {reportType === 'daily' && dailySummary && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  Daily Summary - {selectedStoreId !== 'all' ? stores.find(s => s.id === selectedStoreId)?.name : 'All Stores'} - {format(dateRange.end, 'PPP')}
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = dailySummary.entries.map(e => {
                        const store = stores.find(s => s.id === e.store_id);
                        const fuelSales = calculateFuelSales(e);
                        return {
                          store: store?.name || 'Unknown',
                          store_type: store?.store_type || '',
                          date: e.sales_date,
                          shift: e.shift?.toUpperCase(),
                          total_sales: e.total_sales_amount,
                          shop_sales: getDepartmentAmount(e, 'shop'),
                          fuel_sales: fuelSales,
                          coffee_sales: getDepartmentAmount(e, 'coffe'),
                          adblue_sales: getDepartmentAmount(e, 'ad blue'),
                          cash: getCashAmount(e),
                          card: getCardAmount(e),
                          vouchers: getVouchersAmount(e),
                          lottery_sales: e.lottery_sales || 0,
                          lottery_prize: e.lottery_prize_paid || 0,
                          scratch_card_sales: e.service_amounts?.scratch_cards || 0,
                          scratch_prize: e.scratch_card_prize_paid || 0,
                          paypoint: getServiceAmount(e, 'paypoint'),
                          air_machine: getServiceAmount(e, 'air_machine'),
                          paid_out: e.total_paid_out || 0,
                          variance: e.variance,
                          variance_reason: e.variance_reason || '',
                          submitted_by: getUserName(e.submitted_by)
                        };
                      });
                      exportCSV(exportData, 'daily_summary');
                    }}
                    data-testid="export-daily-csv-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      // Build export data matching screen columns exactly
                      const entries = dailySummary.entries;
                      const selectedStore = selectedStoreId !== 'all' ? stores.find(s => s.id === selectedStoreId) : null;
                      const hasFuelStation = selectedStore 
                        ? selectedStore.store_type === 'fuel_station'
                        : entries.some(e => stores.find(s => s.id === e.store_id)?.store_type === 'fuel_station');
                      
                      // Get visible fuel card columns
                      const visibleFuelCards = hasFuelStation ? fuelCardTypes.filter(fc => 
                        isFieldVisible(`fuel_card_${fc.id}`) && entries.some(e => getFuelCardAmount(e, fc.id) > 0)
                      ) : [];
                      
                      // Build column definitions matching screen order
                      const columns = [];
                      if (isFieldVisible('date')) columns.push({ key: 'date', label: 'Date' });
                      if (isFieldVisible('total_sales')) columns.push({ key: 'total_sales', label: 'Total Sales' });
                      if (isFieldVisible('cash')) columns.push({ key: 'cash', label: 'Cash' });
                      if (isFieldVisible('card')) columns.push({ key: 'card', label: 'Card' });
                      if (isFieldVisible('vouchers') && hasNonZeroValues(entries, e => getVouchersAmount(e))) {
                        columns.push({ key: 'vouchers', label: 'Vouchers' });
                      }
                      if (isFieldVisible('amex') && hasNonZeroValues(entries, e => getAmexAmount(e))) {
                        columns.push({ key: 'amex', label: 'AMEX' });
                      }
                      if (isFieldVisible('staff_expenses') && hasNonZeroValues(entries, e => getStaffExpensesAmount(e))) {
                        columns.push({ key: 'staff_expenses', label: 'Staff Exp' });
                      }
                      // Add visible fuel cards
                      visibleFuelCards.forEach(fc => {
                        columns.push({ key: `fuel_card_${fc.id}`, label: fc.name });
                      });
                      if (isFieldVisible('lottery_sales') && hasNonZeroValues(entries, e => e.service_amounts?.lottery || e.lottery_sales)) {
                        columns.push({ key: 'lottery_sales', label: 'Lottery' });
                      }
                      if (isFieldVisible('scratch_card_sales') && hasNonZeroValues(entries, e => e.service_amounts?.scratch_cards)) {
                        columns.push({ key: 'scratch_card_sales', label: 'Scratch' });
                      }
                      if (isFieldVisible('paypoint') && hasNonZeroValues(entries, e => getServiceAmount(e, 'paypoint'))) {
                        columns.push({ key: 'paypoint', label: 'Paypoint' });
                      }
                      if (isFieldVisible('paid_out') && hasNonZeroValues(entries, e => e.total_paid_out)) {
                        columns.push({ key: 'paid_out', label: 'Paid Out' });
                      }
                      if (isFieldVisible('variance')) columns.push({ key: 'variance', label: 'Variance' });
                      
                      // Build row data
                      const exportData = entries.map(e => {
                        const store = stores.find(s => s.id === e.store_id);
                        const entryVariance = calculateVariance(e, store);
                        const paidOut = e.total_paid_out || 0;
                        
                        const row = {};
                        columns.forEach(col => {
                          switch(col.key) {
                            case 'date': row[col.label] = new Date(e.sales_date).toLocaleDateString('en-GB'); break;
                            case 'total_sales': row[col.label] = formatCurrency(e.total_sales_amount); break;
                            case 'cash': row[col.label] = formatCurrency(getCashAmount(e)); break;
                            case 'card': row[col.label] = formatCurrency(getCardAmount(e)); break;
                            case 'vouchers': row[col.label] = formatCurrency(getVouchersAmount(e)); break;
                            case 'amex': row[col.label] = formatCurrency(getAmexAmount(e)); break;
                            case 'staff_expenses': row[col.label] = formatCurrency(getStaffExpensesAmount(e)); break;
                            case 'lottery_sales': row[col.label] = formatCurrency(e.service_amounts?.lottery || e.lottery_sales || 0); break;
                            case 'scratch_card_sales': row[col.label] = formatCurrency(e.service_amounts?.scratch_cards || 0); break;
                            case 'paypoint': row[col.label] = formatCurrency(getServiceAmount(e, 'paypoint')); break;
                            case 'paid_out': row[col.label] = paidOut > 0 ? `-${formatCurrency(paidOut)}` : formatCurrency(0); break;
                            case 'variance': row[col.label] = `${entryVariance >= 0 ? '+' : ''}${formatCurrency(entryVariance)}`; break;
                            default:
                              // Fuel card columns
                              if (col.key.startsWith('fuel_card_')) {
                                const fcId = col.key.replace('fuel_card_', '');
                                row[col.label] = formatCurrency(getFuelCardAmount(e, fcId));
                              }
                          }
                        });
                        return row;
                      });
                      
                      exportPDF(exportData, 'Daily Sales Summary', 'daily_summary');
                    }}
                    data-testid="export-daily-pdf-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-7 gap-2 md:gap-4 mb-6">
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Total Sales</p>
                    <p className="text-base md:text-lg font-bold font-mono">{formatCurrency(dailySummary.summary.total_sales)}</p>
                  </div>
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Fuel Sales</p>
                    <p className="text-base md:text-lg font-bold font-mono text-amber-400">
                      {formatCurrency(dailySummary.entries.reduce((acc, e) => acc + calculateFuelSales(e), 0))}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Total Tender</p>
                    <p className="text-base md:text-lg font-bold font-mono text-green-400">
                      {formatCurrency(dailySummary.entries.reduce((acc, e) => acc + calculateTender(e), 0))}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Cash</p>
                    <p className="text-base md:text-lg font-bold font-mono">{formatCurrency(dailySummary.summary.total_cash)}</p>
                  </div>
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Card</p>
                    <p className="text-base md:text-lg font-bold font-mono">{formatCurrency(dailySummary.summary.total_card)}</p>
                  </div>
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Vouchers</p>
                    <p className="text-base md:text-lg font-bold font-mono">{formatCurrency(dailySummary.summary.total_vouchers)}</p>
                  </div>
                  <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs md:text-sm text-muted-foreground">Variance</p>
                    <p className={`text-base md:text-lg font-bold font-mono ${
                      Math.abs(dailySummary.entries.reduce((acc, e) => acc + calculateVariance(e, stores.find(s => s.id === e.store_id)), 0)) < 0.01 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {formatCurrency(dailySummary.entries.reduce((acc, e) => acc + calculateVariance(e, stores.find(s => s.id === e.store_id)), 0))}
                    </p>
                  </div>
                </div>

                {dailySummary.entries.length > 0 && (() => {
                  // Pre-calculate which columns have non-zero values to auto-hide empty columns
                  const entries = dailySummary.entries;
                  
                  // Get the selected store for column visibility based on store setup
                  const selectedStore = selectedStoreId !== 'all' 
                    ? stores.find(s => s.id === selectedStoreId) 
                    : null;
                  
                  // Check if any entry's store has a specific setting enabled
                  const anyStoreHas = (checkFn) => {
                    if (selectedStore) return checkFn(selectedStore);
                    return entries.some(e => {
                      const store = stores.find(s => s.id === e.store_id);
                      return store && checkFn(store);
                    });
                  };
                  
                  // Check if any store is a fuel station
                  const hasFuelStation = anyStoreHas(s => s.store_type === 'fuel_station');
                  
                  // Determine which columns are applicable based on store type
                  const showColumn = {
                    // Sales columns
                    fuel_sales: hasFuelStation,  // Only for fuel stations
                    shop_sales: true,
                    coffee_sales: true,
                    adblue_sales: hasFuelStation,
                    // Payment columns - always available
                    cash: true,
                    card: true,
                    vouchers: true,
                    amex: true,
                    staff_expenses: true,
                    // Other columns
                    lottery_sales: true,
                    lottery_prize: true,
                    scratch_card_sales: true,
                    scratch_prize: true,
                    paypoint: true,
                    air_machine: true,
                    paid_out: true,
                  };
                  
                  // Build fuel card columns for fuel stations - show all assigned cards
                  const fuelCardColumns = hasFuelStation ? fuelCardTypes.filter(fc => {
                    // Check if any entry's store has this fuel card assigned
                    return entries.some(e => {
                      const store = stores.find(s => s.id === e.store_id);
                      if (store?.store_type !== 'fuel_station') return false;
                      return store.active_fuel_cards?.some(c => c.card_type_id === fc.id);
                    });
                  }) : [];
                  
                  // Check if column should be visible (user filter + has data)
                  const shouldShowColumn = (key) => isFieldVisible(key) && (showColumn[key] !== false);
                  
                  return (
                  <div className="overflow-x-auto" key={`table-${users.length}`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          {isFieldVisible('store') && <th className="text-left p-3">Store</th>}
                          {isFieldVisible('date') && <th className="text-left p-3">Date</th>}
                          {isFieldVisible('shift') && <th className="text-left p-3">Shift</th>}
                          {isFieldVisible('total_sales') && <th className="text-right p-3">Total Sales</th>}
                          {shouldShowColumn('fuel_sales') && <th className="text-right p-3">Fuel Sales</th>}
                          {shouldShowColumn('shop_sales') && <th className="text-right p-3">Shop Sales</th>}
                          {shouldShowColumn('coffee_sales') && <th className="text-right p-3">Coffee Sales</th>}
                          {shouldShowColumn('adblue_sales') && <th className="text-right p-3">Ad Blue Sales</th>}
                          {/* Payment columns - always show Cash and Card */}
                          {shouldShowColumn('cash') && <th className="text-right p-3">Cash</th>}
                          {shouldShowColumn('card') && <th className="text-right p-3">Card (BMS)</th>}
                          {shouldShowColumn('vouchers') && <th className="text-right p-3">Vouchers</th>}
                          {shouldShowColumn('amex') && <th className="text-right p-3">AMEX</th>}
                          {shouldShowColumn('staff_expenses') && <th className="text-right p-3">Staff Expenses</th>}
                          {/* Individual Fuel Card columns for fuel stations */}
                          {fuelCardColumns.map(fc => (
                            isFieldVisible(`fuel_card_${fc.id}`) && (
                              <th key={fc.id} className="text-right p-3 text-blue-400">{fc.name}</th>
                            )
                          ))}
                          {shouldShowColumn('lottery_sales') && <th className="text-right p-3">Lottery</th>}
                          {shouldShowColumn('lottery_prize') && <th className="text-right p-3 text-red-400">Lottery Prize</th>}
                          {shouldShowColumn('scratch_card_sales') && <th className="text-right p-3">Scratch</th>}
                          {shouldShowColumn('scratch_prize') && <th className="text-right p-3 text-red-400">Scratch Prize</th>}
                          {shouldShowColumn('paypoint') && <th className="text-right p-3">Paypoint</th>}
                          {shouldShowColumn('air_machine') && <th className="text-right p-3">Air Machine</th>}
                          {shouldShowColumn('paid_out') && <th className="text-right p-3">Paid Out</th>}
                          {isFieldVisible('variance') && <th className="text-right p-3">Variance</th>}
                          {isFieldVisible('submitted_by') && <th className="text-left p-3">Submitted By</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry) => {
                          const store = stores.find(s => s.id === entry.store_id);
                          const fuelSales = calculateFuelSales(entry);
                          const entryVariance = calculateVariance(entry, store);
                          return (
                            <tr key={entry.id} className="border-b border-border/50">
                              {isFieldVisible('store') && (
                                <td className="p-3">
                                  {store?.name || 'Unknown'}
                                  {store?.store_type === 'fuel_station' && (
                                    <span className="ml-2 text-xs text-amber-400">⛽</span>
                                  )}
                                </td>
                              )}
                              {isFieldVisible('date') && (
                                <td className="p-3">
                                  {new Date(entry.sales_date).toLocaleDateString('en-GB')}
                                </td>
                              )}
                              {isFieldVisible('shift') && <td className="p-3 uppercase">{entry.shift}</td>}
                              {isFieldVisible('total_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entry.total_sales_amount)}</td>}
                              {shouldShowColumn('fuel_sales') && (
                                <td className={`p-3 text-right font-mono ${fuelSales > 0 ? 'text-amber-400' : ''}`}>
                                  {formatCurrency(fuelSales)}
                                </td>
                              )}
                              {shouldShowColumn('shop_sales') && <td className="p-3 text-right font-mono">{formatCurrency(getDepartmentAmount(entry, 'shop'))}</td>}
                              {shouldShowColumn('coffee_sales') && <td className="p-3 text-right font-mono">{formatCurrency(getDepartmentAmount(entry, 'coffe'))}</td>}
                              {shouldShowColumn('adblue_sales') && <td className="p-3 text-right font-mono">{formatCurrency(getDepartmentAmount(entry, 'ad blue'))}</td>}
                              {/* Payment columns */}
                              {shouldShowColumn('cash') && <td className="p-3 text-right font-mono">{formatCurrency(getCashAmount(entry))}</td>}
                              {shouldShowColumn('card') && <td className="p-3 text-right font-mono">{formatCurrency(getCardAmount(entry))}</td>}
                              {shouldShowColumn('vouchers') && <td className="p-3 text-right font-mono">{formatCurrency(getVouchersAmount(entry))}</td>}
                              {shouldShowColumn('amex') && <td className="p-3 text-right font-mono">{formatCurrency(getAmexAmount(entry))}</td>}
                              {shouldShowColumn('staff_expenses') && <td className="p-3 text-right font-mono">{formatCurrency(getStaffExpensesAmount(entry))}</td>}
                              {/* Individual Fuel Card columns */}
                              {fuelCardColumns.map(fc => (
                                isFieldVisible(`fuel_card_${fc.id}`) && (
                                  <td key={fc.id} className="p-3 text-right font-mono text-blue-400">
                                    {formatCurrency(getFuelCardAmount(entry, fc.id))}
                                  </td>
                                )
                              ))}
                              {shouldShowColumn('lottery_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entry.service_amounts?.lottery || entry.lottery_sales || 0)}</td>}
                              {shouldShowColumn('lottery_prize') && <td className="p-3 text-right font-mono text-red-400">{formatCurrency(entry.lottery_prize_paid || 0)}</td>}
                              {shouldShowColumn('scratch_card_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entry.service_amounts?.scratch_cards || 0)}</td>}
                              {shouldShowColumn('scratch_prize') && <td className="p-3 text-right font-mono text-red-400">{formatCurrency(entry.scratch_card_prize_paid || 0)}</td>}
                              {shouldShowColumn('paypoint') && <td className="p-3 text-right font-mono">{formatCurrency(getServiceAmount(entry, 'paypoint'))}</td>}
                              {shouldShowColumn('air_machine') && <td className="p-3 text-right font-mono">{formatCurrency(getServiceAmount(entry, 'air_machine'))}</td>}
                              {shouldShowColumn('paid_out') && (
                                <td className="p-3 text-right font-mono text-red-400">
                                  {(entry.total_paid_out || 0) > 0 ? '-' : ''}{formatCurrency(entry.total_paid_out || 0)}
                                </td>
                              )}
                              {isFieldVisible('variance') && (
                                <td className={`p-3 text-right font-mono ${Math.abs(entryVariance) >= 0.01 ? (entryVariance > 0 ? 'text-green-400' : 'text-red-400') : ''}`}>
                                  {entryVariance >= 0 ? '+' : ''}{formatCurrency(entryVariance)}
                                </td>
                              )}
                              {isFieldVisible('submitted_by') && <td className="p-3">{getUserName(entry.submitted_by)}</td>}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted/50 font-semibold">
                          <td className="p-3" colSpan={[isFieldVisible('store'), isFieldVisible('date'), isFieldVisible('shift')].filter(Boolean).length || 1}>TOTALS</td>
                          {isFieldVisible('total_sales') && <td className="p-3 text-right font-mono">{formatCurrency(dailySummary.summary.total_sales)}</td>}
                          {shouldShowColumn('fuel_sales') && <td className="p-3 text-right font-mono text-amber-400">{formatCurrency(entries.reduce((a, e) => a + calculateFuelSales(e), 0))}</td>}
                          {shouldShowColumn('shop_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getDepartmentAmount(e, 'shop'), 0))}</td>}
                          {shouldShowColumn('coffee_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getDepartmentAmount(e, 'coffe'), 0))}</td>}
                          {shouldShowColumn('adblue_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getDepartmentAmount(e, 'ad blue'), 0))}</td>}
                          {/* Payment totals */}
                          {shouldShowColumn('cash') && <td className="p-3 text-right font-mono">{formatCurrency(dailySummary.summary.total_cash)}</td>}
                          {shouldShowColumn('card') && <td className="p-3 text-right font-mono">{formatCurrency(dailySummary.summary.total_card)}</td>}
                          {shouldShowColumn('vouchers') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getVouchersAmount(e), 0))}</td>}
                          {shouldShowColumn('amex') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getAmexAmount(e), 0))}</td>}
                          {shouldShowColumn('staff_expenses') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getStaffExpensesAmount(e), 0))}</td>}
                          {/* Fuel Card totals */}
                          {fuelCardColumns.map(fc => (
                            isFieldVisible(`fuel_card_${fc.id}`) && (
                              <td key={fc.id} className="p-3 text-right font-mono text-blue-400">
                                {formatCurrency(entries.reduce((a, e) => a + getFuelCardAmount(e, fc.id), 0))}
                              </td>
                            )
                          ))}
                          {shouldShowColumn('lottery_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + (e.service_amounts?.lottery || e.lottery_sales || 0), 0))}</td>}
                          {shouldShowColumn('lottery_prize') && <td className="p-3 text-right font-mono text-red-400">{formatCurrency(entries.reduce((a, e) => a + (e.lottery_prize_paid || 0), 0))}</td>}
                          {shouldShowColumn('scratch_card_sales') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + (e.service_amounts?.scratch_cards || 0), 0))}</td>}
                          {shouldShowColumn('scratch_prize') && <td className="p-3 text-right font-mono text-red-400">{formatCurrency(entries.reduce((a, e) => a + (e.scratch_card_prize_paid || 0), 0))}</td>}
                          {shouldShowColumn('paypoint') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getServiceAmount(e, 'paypoint'), 0))}</td>}
                          {shouldShowColumn('air_machine') && <td className="p-3 text-right font-mono">{formatCurrency(entries.reduce((a, e) => a + getServiceAmount(e, 'air_machine'), 0))}</td>}
                          {shouldShowColumn('paid_out') && (() => {
                            const totalPaidOut = entries.reduce((a, e) => a + (e.total_paid_out || 0), 0);
                            return (
                              <td className="p-3 text-right font-mono text-red-400">
                                {totalPaidOut > 0 ? '-' : ''}{formatCurrency(totalPaidOut)}
                              </td>
                            );
                          })()}
                          {isFieldVisible('variance') && (() => {
                            const totalVariance = entries.reduce((a, e) => a + calculateVariance(e, stores.find(s => s.id === e.store_id)), 0);
                            return (
                              <td className={`p-3 text-right font-mono ${Math.abs(totalVariance) >= 0.01 ? (totalVariance > 0 ? 'text-green-400' : 'text-red-400') : ''}`}>
                                {totalVariance >= 0 ? '+' : ''}{formatCurrency(totalVariance)}
                              </td>
                            );
                          })()}
                          {isFieldVisible('submitted_by') && <td className="p-3">-</td>}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Store Summary */}
          {reportType === 'store' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Store Summary</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportCSV(storeSummary, 'store_summary')}
                    data-testid="export-store-csv-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportPDF(storeSummary, 'Store Summary Report', 'store_summary')}
                    data-testid="export-store-pdf-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {storeSummary.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No data for selected period</p>
                ) : (
                  <>
                    <div className="chart-container mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={storeSummary}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis dataKey="store_name" stroke="#71717a" tick={{ fontSize: 12 }} />
                          <YAxis stroke="#71717a" tick={{ fontSize: 12 }} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Legend />
                          <Bar dataKey="total_sales" name="Sales" fill="#f97316" />
                          <Bar dataKey="total_cash" name="Cash" fill="#10b981" />
                          <Bar dataKey="total_card" name="Card" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            {isFieldVisible('store_name') && <th className="text-left p-3">Store</th>}
                            {isFieldVisible('total_sales') && <th className="text-right p-3">Total Sales</th>}
                            {isFieldVisible('total_cash') && <th className="text-right p-3">Cash</th>}
                            {isFieldVisible('total_card') && <th className="text-right p-3">Card</th>}
                            <th className="text-right p-3">Paid Out</th>
                            {isFieldVisible('total_variance') && <th className="text-right p-3">Variance</th>}
                            <th className="text-right p-3">Entries</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeSummary.map((store) => (
                            <tr key={store.store_id} className="border-b border-border/50">
                              {isFieldVisible('store_name') && <td className="p-3 font-medium">{store.store_name}</td>}
                              {isFieldVisible('total_sales') && <td className="p-3 text-right font-mono">{formatCurrency(store.total_sales)}</td>}
                              {isFieldVisible('total_cash') && <td className="p-3 text-right font-mono">{formatCurrency(store.total_cash)}</td>}
                              {isFieldVisible('total_card') && <td className="p-3 text-right font-mono">{formatCurrency(store.total_card)}</td>}
                              <td className="p-3 text-right font-mono">{formatCurrency(store.total_paid_out)}</td>
                              {isFieldVisible('total_variance') && (
                                <td className={`p-3 text-right font-mono ${store.total_variance !== 0 ? 'text-red-400' : ''}`}>
                                  {formatCurrency(store.total_variance)}
                                </td>
                              )}
                              <td className="p-3 text-right">{store.entry_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Variance Report */}
          {reportType === 'variance' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Variance Report
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportCSV(varianceReport, 'variance_report')}
                    data-testid="export-variance-csv-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportPDF(varianceReport, 'Variance Report', 'variance_report')}
                    data-testid="export-variance-pdf-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {varianceReport.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No variances in selected period</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          {isFieldVisible('date') && <th className="text-left p-3">Date</th>}
                          {isFieldVisible('store') && <th className="text-left p-3">Store</th>}
                          {isFieldVisible('shift') && <th className="text-left p-3">Shift</th>}
                          {isFieldVisible('variance') && <th className="text-right p-3">Variance</th>}
                          {isFieldVisible('reason') && <th className="text-left p-3">Reason</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {varianceReport.map((entry) => (
                          <tr key={entry.id} className="border-b border-border/50">
                            {isFieldVisible('date') && (
                              <td className="p-3">
                                {new Date(entry.sales_date).toLocaleDateString('en-GB')}
                              </td>
                            )}
                            {isFieldVisible('store') && <td className="p-3">{entry.store_name}</td>}
                            {isFieldVisible('shift') && <td className="p-3 uppercase">{entry.shift}</td>}
                            {isFieldVisible('variance') && (
                              <td className={`p-3 text-right font-mono font-medium ${
                                entry.variance > 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {formatCurrency(entry.variance)}
                              </td>
                            )}
                            {isFieldVisible('reason') && <td className="p-3 text-muted-foreground">{entry.variance_reason || '-'}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Sales Trend */}
          {reportType === 'trend' && (
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Sales Trend (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={salesTrend}>
                      <defs>
                        <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#71717a"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      />
                      <YAxis 
                        stroke="#71717a"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                        formatter={(value) => [formatCurrency(value), 'Sales']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="total_sales" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        fill="url(#salesGradient)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Period Comparison */}
          {reportType === 'compare' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Period Comparison</CardTitle>
                {compareData && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const exportData = [
                          {
                            period: 'Period 1',
                            date_range: `${format(new Date(compareData.period1.start), 'PP')} - ${format(new Date(compareData.period1.end), 'PP')}`,
                            total_sales: compareData.period1.total_sales,
                            total_cash: compareData.period1.total_cash,
                            total_card: compareData.period1.total_card,
                            total_variance: compareData.period1.total_variance,
                            entries: compareData.period1.entry_count
                          },
                          {
                            period: 'Period 2',
                            date_range: `${format(new Date(compareData.period2.start), 'PP')} - ${format(new Date(compareData.period2.end), 'PP')}`,
                            total_sales: compareData.period2.total_sales,
                            total_cash: compareData.period2.total_cash,
                            total_card: compareData.period2.total_card,
                            total_variance: compareData.period2.total_variance,
                            entries: compareData.period2.entry_count
                          },
                          {
                            period: 'Change',
                            date_range: '-',
                            total_sales: compareData.comparison.sales_difference,
                            total_cash: '-',
                            total_card: '-',
                            total_variance: '-',
                            entries: `${compareData.comparison.sales_percentage_change}%`
                          }
                        ];
                        exportCSV(exportData, 'period_comparison');
                      }}
                      data-testid="export-comparison-csv-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const exportData = [
                          {
                            period: 'Period 1',
                            date_range: `${format(new Date(compareData.period1.start), 'PP')} - ${format(new Date(compareData.period1.end), 'PP')}`,
                            total_sales: compareData.period1.total_sales,
                            total_cash: compareData.period1.total_cash,
                            total_card: compareData.period1.total_card,
                            total_variance: compareData.period1.total_variance,
                            entries: compareData.period1.entry_count
                          },
                          {
                            period: 'Period 2',
                            date_range: `${format(new Date(compareData.period2.start), 'PP')} - ${format(new Date(compareData.period2.end), 'PP')}`,
                            total_sales: compareData.period2.total_sales,
                            total_cash: compareData.period2.total_cash,
                            total_card: compareData.period2.total_card,
                            total_variance: compareData.period2.total_variance,
                            entries: compareData.period2.entry_count
                          },
                          {
                            period: 'Change',
                            date_range: '-',
                            total_sales: compareData.comparison.sales_difference,
                            total_cash: '-',
                            total_card: '-',
                            total_variance: '-',
                            entries: `${compareData.comparison.sales_percentage_change}%`
                          }
                        ];
                        exportPDF(exportData, 'Period Comparison Report', 'period_comparison');
                      }}
                      data-testid="export-comparison-pdf-btn"
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label>Period 1</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(period1.start, 'PP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={period1.start}
                            onSelect={(date) => date && setPeriod1({ ...period1, start: date })}
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(period1.end, 'PP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={period1.end}
                            onSelect={(date) => date && setPeriod1({ ...period1, end: date })}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Period 2</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(period2.start, 'PP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={period2.start}
                            onSelect={(date) => date && setPeriod2({ ...period2, start: date })}
                          />
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(period2.end, 'PP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={period2.end}
                            onSelect={(date) => date && setPeriod2({ ...period2, end: date })}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                <Button onClick={fetchComparison} className="w-full" data-testid="compare-periods-btn">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Compare Periods
                </Button>

                {compareData && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Period 1</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(compareData.period1.start), 'PP')} - {format(new Date(compareData.period1.end), 'PP')}
                      </p>
                      <p className="text-2xl font-bold font-mono mt-2">{formatCurrency(compareData.period1.total_sales)}</p>
                      <p className="text-sm text-muted-foreground">{compareData.period1.entry_count} entries</p>
                    </div>

                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Period 2</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(compareData.period2.start), 'PP')} - {format(new Date(compareData.period2.end), 'PP')}
                      </p>
                      <p className="text-2xl font-bold font-mono mt-2">{formatCurrency(compareData.period2.total_sales)}</p>
                      <p className="text-sm text-muted-foreground">{compareData.period2.entry_count} entries</p>
                    </div>

                    <div className={`p-4 rounded-lg ${
                      compareData.comparison.sales_percentage_change >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}>
                      <p className="text-sm text-muted-foreground mb-2">Change</p>
                      <p className={`text-2xl font-bold font-mono ${
                        compareData.comparison.sales_percentage_change >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {compareData.comparison.sales_percentage_change >= 0 ? '+' : ''}
                        {compareData.comparison.sales_percentage_change}%
                      </p>
                      <p className="text-sm font-mono">
                        {compareData.comparison.sales_difference >= 0 ? '+' : ''}
                        {formatCurrency(compareData.comparison.sales_difference)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Creditors & Investments Report */}
          {/* Creditor Report */}
          {reportType === 'lending' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                    Creditor Report{selectedCreditor !== 'all' ? ` - ${creditors.find(c => c.id === selectedCreditor)?.name || ''}` : ''}
                  </CardTitle>
                  {showFromLastPayment && selectedCreditor !== 'all' && (
                    <p className="text-xs text-muted-foreground mt-1">Showing transactions since last repayment</p>
                  )}
                  {creditorTransactionType !== 'all' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Filter: {creditorTransactionType === 'credit' ? 'Credit Only' : 'Repayment Only'}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredCreditorReport.map(t => ({
                        transaction_id: t.transaction_id || '-',
                        date: t.date,
                        creditor_name: t.entity_name || '-',
                        description: t.description || '-',
                        credit: t.direction === 'out' ? t.amount : 0,
                        repayment: t.direction === 'in' ? t.amount : 0,
                        source: (t.source || 'cash').replace('_', ' '),
                        entered_by: t.created_by_name || '-'
                      }));
                      exportCSV(exportData, 'creditor_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredCreditorReport.map(t => ({
                        transaction_id: t.transaction_id || '-',
                        date: t.date,
                        creditor_name: t.entity_name || '-',
                        description: t.description || '-',
                        credit: t.direction === 'out' ? t.amount : 0,
                        repayment: t.direction === 'in' ? t.amount : 0,
                        source: (t.source || 'cash').replace('_', ' '),
                        entered_by: t.created_by_name || '-'
                      }));
                      const creditorName = selectedCreditor !== 'all' ? creditors.find(c => c.id === selectedCreditor)?.name : '';
                      const reportTitle = `Creditor Report${creditorName ? ` - ${creditorName}` : ''}`;
                      
                      // Generate PDF with Outstanding Balance
                      const doc = generatePDFDocument(exportData, reportTitle, true);
                      
                      // Add Outstanding Balance section after table
                      const outstanding = getCreditorOutstanding();
                      const finalY = doc.lastAutoTable?.finalY || 150;
                      
                      // Draw outstanding balance box - Dark blue background for better visibility
                      doc.setFillColor(30, 58, 138); // Dark blue background
                      doc.rect(14, finalY + 10, doc.internal.pageSize.width - 28, 25, 'F');
                      doc.setDrawColor(59, 130, 246); // Blue border
                      doc.rect(14, finalY + 10, doc.internal.pageSize.width - 28, 25, 'S');
                      
                      doc.setFontSize(14);
                      doc.setTextColor(255, 255, 255); // White text
                      doc.text('TOTAL OUTSTANDING BALANCE', 20, finalY + 22);
                      
                      doc.setFontSize(12);
                      doc.setTextColor(200, 200, 200); // Light gray
                      const balanceLabel = selectedCreditor !== 'all' 
                        ? `All-time balance for ${creditorName || 'this creditor'}`
                        : 'Combined outstanding balance for all creditors';
                      doc.text(balanceLabel, 20, finalY + 30);
                      
                      doc.setFontSize(16);
                      if (outstanding > 0) {
                        doc.setTextColor(251, 191, 36); // Bright amber/yellow
                      } else {
                        doc.setTextColor(74, 222, 128); // Bright green
                      }
                      doc.text(formatCurrency(outstanding), doc.internal.pageSize.width - 20, finalY + 25, { align: 'right' });
                      
                      doc.save(`creditor_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
                      toast.success('PDF exported successfully');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCreditorReport.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border bg-muted/30">
                            {isFieldVisible('transaction_id') && <th className="text-left p-3 text-sm font-medium">ID</th>}
                            {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                            {isFieldVisible('name') && <th className="text-left p-3 text-sm font-medium">Creditor Name</th>}
                            {isFieldVisible('description') && <th className="text-left p-3 text-sm font-medium">Description</th>}
                            <th className="text-right p-3 text-sm font-medium text-red-400">Credit</th>
                            <th className="text-right p-3 text-sm font-medium text-green-400">Repayment</th>
                            {isFieldVisible('source') && <th className="text-left p-3 text-sm font-medium">Source</th>}
                            {isFieldVisible('entered_by') && <th className="text-left p-3 text-sm font-medium">Entered By</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCreditorReport.map((t, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                              {isFieldVisible('transaction_id') && <td className="p-3 text-sm font-mono">{t.transaction_id || '-'}</td>}
                              {isFieldVisible('date') && <td className="p-3 text-sm">{t.date}</td>}
                              {isFieldVisible('name') && <td className="p-3 text-sm font-medium">{t.entity_name || '-'}</td>}
                              {isFieldVisible('description') && <td className="p-3 text-sm text-muted-foreground">{t.description || '-'}</td>}
                              <td className="p-3 text-right font-mono text-sm text-red-400">
                                {t.direction === 'out' ? formatCurrency(t.amount) : '-'}
                              </td>
                              <td className="p-3 text-right font-mono text-sm text-green-400">
                                {t.direction === 'in' ? formatCurrency(t.amount) : '-'}
                              </td>
                              {isFieldVisible('source') && <td className="p-3 text-sm capitalize">{(t.source || 'cash').replace('_', ' ')}</td>}
                              {isFieldVisible('entered_by') && <td className="p-3 text-sm">{t.created_by_name || '-'}</td>}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {/* Report Totals Row */}
                          <tr className="bg-muted/50 border-t-2 border-border">
                            <td colSpan={isFieldVisible('transaction_id') ? 1 : 0} className="p-3"></td>
                            {isFieldVisible('date') && <td className="p-3"></td>}
                            {isFieldVisible('name') && <td className="p-3"></td>}
                            {isFieldVisible('description') && <td className="p-3 text-sm font-semibold">REPORT TOTAL</td>}
                            {!isFieldVisible('description') && <td className="p-3 text-sm font-semibold">TOTAL</td>}
                            <td className="p-3 text-right font-mono text-sm font-bold text-red-400">
                              {formatCurrency(filteredCreditorReport.filter(t => t.direction === 'out').reduce((a, t) => a + (t.amount || 0), 0))}
                            </td>
                            <td className="p-3 text-right font-mono text-sm font-bold text-green-400">
                              {formatCurrency(filteredCreditorReport.filter(t => t.direction === 'in').reduce((a, t) => a + (t.amount || 0), 0))}
                            </td>
                            {isFieldVisible('source') && <td className="p-3"></td>}
                            {isFieldVisible('entered_by') && <td className="p-3"></td>}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    
                    {/* Total Outstanding Balance */}
                    <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-bold">TOTAL OUTSTANDING BALANCE</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedCreditor !== 'all' 
                              ? `All-time balance for ${creditors.find(c => c.id === selectedCreditor)?.name || 'this creditor'}`
                              : 'Combined outstanding balance for all creditors'}
                          </p>
                        </div>
                        <p className={`text-2xl font-bold font-mono ${getCreditorOutstanding() > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                          {formatCurrency(getCreditorOutstanding())}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No creditor transactions found{selectedCreditor !== 'all' ? ` for ${creditors.find(c => c.id === selectedCreditor)?.name}` : ''}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Investments Report */}
          {reportType === 'investments' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  Investments Report{selectedScheme !== 'all' ? ` - ${schemes.find(s => s.id === selectedScheme)?.name || ''}` : ''}
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredInvestmentReport.map(t => ({
                        transaction_id: t.transaction_id || '-',
                        date: t.date,
                        name: t.scheme_name || '-',
                        direction: t.direction === 'out' ? 'Invested' : 'Returned',
                        amount: t.amount,
                        source: t.source || 'cash',
                        description: t.description || '',
                        entered_by: getUserName(t.user_id)
                      }));
                      exportCSV(exportData, 'investments_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredInvestmentReport.map(t => ({
                        transaction_id: t.transaction_id || '-',
                        date: t.date,
                        name: t.scheme_name || '-',
                        direction: t.direction === 'out' ? 'Invested' : 'Returned',
                        amount: t.amount,
                        source: t.source || 'cash',
                        description: t.description || '',
                        entered_by: getUserName(t.user_id)
                      }));
                      const schemeName = selectedScheme !== 'all' ? schemes.find(s => s.id === selectedScheme)?.name : '';
                      exportPDF(exportData, `Investments Report${schemeName ? ` - ${schemeName}` : ''}`, 'investments_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {filteredInvestmentReport.length > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Invested{selectedScheme !== 'all' ? ` (${schemes.find(s => s.id === selectedScheme)?.name})` : ''}</p>
                        <p className="text-xl font-bold font-mono text-purple-400">
                          {formatCurrency(filteredInvestmentReport.filter(t => t.direction === 'out').reduce((a, t) => a + (t.amount || 0), 0))}
                        </p>
                      </div>
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Returned</p>
                        <p className="text-xl font-bold font-mono text-green-400">
                          {formatCurrency(filteredInvestmentReport.filter(t => t.direction === 'in').reduce((a, t) => a + (t.amount || 0), 0))}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Outstanding</p>
                        <p className="text-xl font-bold font-mono text-blue-400">
                          {formatCurrency(
                            filteredInvestmentReport.filter(t => t.direction === 'out').reduce((a, t) => a + (t.amount || 0), 0) -
                            filteredInvestmentReport.filter(t => t.direction === 'in').reduce((a, t) => a + (t.amount || 0), 0)
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            {isFieldVisible('transaction_id') && <th className="text-left p-3 text-sm font-medium">ID</th>}
                            {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                            {isFieldVisible('name') && <th className="text-left p-3 text-sm font-medium">Scheme</th>}
                            {isFieldVisible('direction') && <th className="text-left p-3 text-sm font-medium">Direction</th>}
                            {isFieldVisible('amount') && <th className="text-right p-3 text-sm font-medium">Amount</th>}
                            {isFieldVisible('source') && <th className="text-left p-3 text-sm font-medium">Source</th>}
                            {isFieldVisible('entered_by') && <th className="text-left p-3 text-sm font-medium">Entered By</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredInvestmentReport.map((t, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                              {isFieldVisible('transaction_id') && <td className="p-3 text-sm font-mono">{t.transaction_id || '-'}</td>}
                              {isFieldVisible('date') && <td className="p-3 text-sm">{t.date}</td>}
                              {isFieldVisible('name') && <td className="p-3 text-sm">{t.scheme_name || '-'}</td>}
                              {isFieldVisible('direction') && (
                                <td className="p-3 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${t.direction === 'out' ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'}`}>
                                    {t.direction === 'out' ? 'Invested' : 'Returned'}
                                  </span>
                                </td>
                              )}
                              {isFieldVisible('amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(t.amount)}</td>}
                              {isFieldVisible('source') && <td className="p-3 text-sm capitalize">{t.source || 'cash'}</td>}
                              {isFieldVisible('entered_by') && <td className="p-3 text-sm">{getUserName(t.user_id)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No investment transactions found{selectedScheme !== 'all' ? ` for ${schemes.find(s => s.id === selectedScheme)?.name}` : ''}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Cash Control Report */}
          {reportType === 'cashcontrol' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Cash Control Report</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = cashControlReport.map(t => ({
                        transaction_id: t.transaction_id || '-',
                        date: t.date,
                        transfer_type: t.transfer_type,
                        amount: t.amount,
                        source: t.source || 'cash',
                        description: t.description || '',
                        reference: t.reference || '',
                        entered_by: getUserName(t.user_id)
                      }));
                      exportCSV(exportData, 'cash_control_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = cashControlReport.map(t => ({
                        transaction_id: t.transaction_id || '-',
                        date: t.date,
                        transfer_type: t.transfer_type,
                        amount: t.amount,
                        source: t.source || 'cash',
                        description: t.description || '',
                        reference: t.reference || '',
                        entered_by: getUserName(t.user_id)
                      }));
                      exportPDF(exportData, 'Cash Control Report', 'cash_control_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {cashControlReport.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          {isFieldVisible('transaction_id') && <th className="text-left p-3 text-sm font-medium">ID</th>}
                          {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                          {isFieldVisible('transfer_type') && <th className="text-left p-3 text-sm font-medium">Type</th>}
                          {isFieldVisible('amount') && <th className="text-right p-3 text-sm font-medium">Amount</th>}
                          {isFieldVisible('source') && <th className="text-left p-3 text-sm font-medium">Source</th>}
                          {isFieldVisible('description') && <th className="text-left p-3 text-sm font-medium">Description</th>}
                          {isFieldVisible('reference') && <th className="text-left p-3 text-sm font-medium">Reference</th>}
                          {isFieldVisible('entered_by') && <th className="text-left p-3 text-sm font-medium">Entered By</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {cashControlReport.map((t, idx) => (
                          <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                            {isFieldVisible('transaction_id') && <td className="p-3 text-sm font-mono">{t.transaction_id || '-'}</td>}
                            {isFieldVisible('date') && <td className="p-3 text-sm">{t.date}</td>}
                            {isFieldVisible('transfer_type') && <td className="p-3 text-sm">{t.transfer_type}</td>}
                            {isFieldVisible('amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(t.amount)}</td>}
                            {isFieldVisible('source') && <td className="p-3 text-sm capitalize">{t.source || 'cash'}</td>}
                            {isFieldVisible('description') && <td className="p-3 text-sm">{t.description || '-'}</td>}
                            {isFieldVisible('reference') && <td className="p-3 text-sm">{t.reference || '-'}</td>}
                            {isFieldVisible('entered_by') && <td className="p-3 text-sm">{getUserName(t.user_id)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No cash control transactions found</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Purchases Report */}
          {reportType === 'purchases' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Purchases Report</CardTitle>
                <div className="flex gap-2 items-center">
                  <Select value={purchaseTypeFilter} onValueChange={(val) => {
                    setPurchaseTypeFilter(val);
                  }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Purchase Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Purchase Types</SelectItem>
                      {purchaseTypes.filter(t => t.is_active).map(type => (
                        <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = purchaseReport.map(p => ({
                        date: p.date,
                        supplier: p.supplier_name || '-',
                        invoice_number: p.invoice_number || '-',
                        purchase_type: p.purchase_type || '-',
                        net_amount: p.net_amount,
                        vat_amount: p.vat_amount,
                        total_amount: p.total_amount,
                        payment_type: p.payment_type || '-',
                        description: p.description || '',
                        entered_by: getUserName(p.user_id)
                      }));
                      exportCSV(exportData, 'purchases_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = purchaseReport.map(p => ({
                        date: p.date,
                        supplier: p.supplier_name || '-',
                        invoice_number: p.invoice_number || '-',
                        purchase_type: p.purchase_type || '-',
                        net_amount: p.net_amount,
                        vat_amount: p.vat_amount,
                        total_amount: p.total_amount,
                        payment_type: p.payment_type || '-',
                        description: p.description || '',
                        entered_by: getUserName(p.user_id)
                      }));
                      exportPDF(exportData, 'Purchases Report', 'purchases_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {purchaseReport.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                          {isFieldVisible('supplier') && <th className="text-left p-3 text-sm font-medium">Supplier</th>}
                          {isFieldVisible('invoice_number') && <th className="text-left p-3 text-sm font-medium">Invoice #</th>}
                          <th className="text-left p-3 text-sm font-medium">Type</th>
                          {isFieldVisible('net_amount') && <th className="text-right p-3 text-sm font-medium">Net</th>}
                          {isFieldVisible('vat_amount') && <th className="text-right p-3 text-sm font-medium">VAT</th>}
                          {isFieldVisible('total_amount') && <th className="text-right p-3 text-sm font-medium">Total</th>}
                          {isFieldVisible('payment_type') && <th className="text-left p-3 text-sm font-medium">Payment</th>}
                          {isFieldVisible('entered_by') && <th className="text-left p-3 text-sm font-medium">Entered By</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseReport.map((p, idx) => (
                          <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                            {isFieldVisible('date') && <td className="p-3 text-sm">{p.date}</td>}
                            {isFieldVisible('supplier') && <td className="p-3 text-sm">{p.supplier_name || '-'}</td>}
                            {isFieldVisible('invoice_number') && <td className="p-3 text-sm">{p.invoice_number || '-'}</td>}
                            <td className="p-3 text-sm">
                              <span className="px-2 py-1 bg-muted rounded text-xs">{p.purchase_type || '-'}</span>
                            </td>
                            {isFieldVisible('net_amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(p.net_amount)}</td>}
                            {isFieldVisible('vat_amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(p.vat_amount)}</td>}
                            {isFieldVisible('total_amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(p.total_amount)}</td>}
                            {isFieldVisible('payment_type') && <td className="p-3 text-sm">{p.payment_type || '-'}</td>}
                            {isFieldVisible('entered_by') && <td className="p-3 text-sm">{getUserName(p.user_id)}</td>}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-border font-semibold">
                          <td colSpan={4} className="p-3 text-sm">Total</td>
                          {isFieldVisible('net_amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(purchaseReport.reduce((a, p) => a + (p.net_amount || 0), 0))}</td>}
                          {isFieldVisible('vat_amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(purchaseReport.reduce((a, p) => a + (p.vat_amount || 0), 0))}</td>}
                          {isFieldVisible('total_amount') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(purchaseReport.reduce((a, p) => a + (p.total_amount || 0), 0))}</td>}
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No purchases found</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Coffee Tracking Report */}
          {reportType === 'coffee_tracking' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Coffee Tracking Report</CardTitle>
                <div className="flex gap-2 items-center">
                  {/* Cup Size Filter */}
                  <Select value={selectedCupSize} onValueChange={setSelectedCupSize}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Cup Size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sizes</SelectItem>
                      <SelectItem value="Small">Small</SelectItem>
                      <SelectItem value="Large">Large</SelectItem>
                      <SelectItem value="Total">Total</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const filteredData = selectedCupSize === 'all' 
                        ? coffeeTrackingReport 
                        : coffeeTrackingReport.filter(c => c.cup_size === selectedCupSize);
                      const exportData = filteredData.map(c => ({
                        date: c.date,
                        shift: c.shift,
                        cup_size: c.cup_size,
                        opening_reading: c.opening_reading,
                        closing_reading: c.closing_reading,
                        machine_sold: c.machine_sold,
                        pos_qty: c.pos_qty,
                        variance: c.variance,
                        submitted_by: c.submitted_by_name || '-'
                      }));
                      exportCSV(exportData, 'coffee_tracking_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const filteredData = selectedCupSize === 'all' 
                        ? coffeeTrackingReport 
                        : coffeeTrackingReport.filter(c => c.cup_size === selectedCupSize);
                      const exportData = filteredData.map(c => ({
                        date: c.date,
                        shift: c.shift,
                        cup_size: c.cup_size,
                        opening_reading: c.opening_reading,
                        closing_reading: c.closing_reading,
                        machine_sold: c.machine_sold,
                        pos_qty: c.pos_qty,
                        variance: c.variance,
                        submitted_by: c.submitted_by_name || '-'
                      }));
                      exportPDF(exportData, 'Coffee Tracking Report', 'coffee_tracking_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {coffeeTrackingReport.length > 0 ? (
                  <div className="overflow-x-auto">
                    {/* Summary Cards - based on filtered data */}
                    {(() => {
                      const filteredData = selectedCupSize === 'all' 
                        ? coffeeTrackingReport 
                        : coffeeTrackingReport.filter(c => c.cup_size === selectedCupSize);
                      const summaryData = selectedCupSize === 'all' 
                        ? coffeeTrackingReport.filter(c => c.cup_size === 'Total')
                        : filteredData;
                      return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Total Machine Sold</p>
                        <p className="text-xl font-bold font-mono">
                          {summaryData.reduce((a, c) => a + (c.machine_sold || 0), 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Total POS Qty</p>
                        <p className="text-xl font-bold font-mono">
                          {summaryData.reduce((a, c) => a + (c.pos_qty || 0), 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Total Variance</p>
                        <p className={`text-xl font-bold font-mono ${summaryData.reduce((a, c) => a + (c.variance || 0), 0) !== 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {summaryData.reduce((a, c) => a + (c.variance || 0), 0)}
                        </p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Days with Variance</p>
                        <p className="text-xl font-bold font-mono text-amber-500">
                          {summaryData.filter(c => c.variance !== 0).length}
                        </p>
                      </div>
                    </div>
                      );
                    })()}
                    
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-3 text-sm font-medium">Date</th>
                          <th className="text-left p-3 text-sm font-medium">Shift</th>
                          <th className="text-left p-3 text-sm font-medium">Size</th>
                          <th className="text-right p-3 text-sm font-medium">Opening</th>
                          <th className="text-right p-3 text-sm font-medium">Closing</th>
                          <th className="text-right p-3 text-sm font-medium">Machine Sold</th>
                          <th className="text-right p-3 text-sm font-medium">POS Qty</th>
                          <th className="text-right p-3 text-sm font-medium">Variance</th>
                          <th className="text-left p-3 text-sm font-medium">Submitted By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coffeeTrackingReport
                          .filter(c => selectedCupSize === 'all' || c.cup_size === selectedCupSize)
                          .map((c, idx) => (
                          <tr key={idx} className={`border-b border-border/50 hover:bg-muted/50 ${c.cup_size === 'Total' ? 'bg-muted/30 font-semibold' : ''}`}>
                            <td className="p-3 text-sm">{c.date}</td>
                            <td className="p-3 text-sm capitalize">{c.shift}</td>
                            <td className="p-3 text-sm">
                              <span className={`px-2 py-1 rounded text-xs ${
                                c.cup_size === 'Small' ? 'bg-blue-500/20 text-blue-400' : 
                                c.cup_size === 'Large' ? 'bg-purple-500/20 text-purple-400' : 
                                c.cup_size === 'Total' ? 'bg-green-500/20 text-green-400' : 'bg-muted'
                              }`}>
                                {c.cup_size}
                              </span>
                            </td>
                            <td className="p-3 text-right font-mono text-sm">{c.opening_reading}</td>
                            <td className="p-3 text-right font-mono text-sm">{c.closing_reading}</td>
                            <td className="p-3 text-right font-mono text-sm">{c.machine_sold}</td>
                            <td className="p-3 text-right font-mono text-sm">{c.pos_qty !== null ? c.pos_qty : '-'}</td>
                            <td className={`p-3 text-right font-mono text-sm ${c.variance !== null && c.variance !== 0 ? 'text-red-500 font-bold' : c.variance === 0 ? 'text-green-500' : ''}`}>
                              {c.variance !== null ? c.variance : '-'}
                            </td>
                            <td className="p-3 text-sm">{c.submitted_by_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No coffee tracking data found</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Scratch Card Settlements Report */}
          {reportType === 'scratch_settlements' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Scratch Card Settlements Report</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = scratchSettlementReport.map(s => ({
                        transaction_id: s.transaction_id || '-',
                        date: s.date,
                        total_cards: s.total_cards,
                        total_value: s.total_value,
                        entered_by: getUserName(s.user_id)
                      }));
                      exportCSV(exportData, 'scratch_settlements_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = scratchSettlementReport.map(s => ({
                        transaction_id: s.transaction_id || '-',
                        date: s.date,
                        total_cards: s.total_cards,
                        total_value: s.total_value,
                        entered_by: getUserName(s.user_id)
                      }));
                      exportPDF(exportData, 'Scratch Card Settlements Report', 'scratch_settlements_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {scratchSettlementReport.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Cards Settled</p>
                        <p className="text-xl font-bold font-mono text-blue-400">
                          {scratchSettlementReport.reduce((a, s) => a + (s.total_cards || 0), 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Value</p>
                        <p className="text-xl font-bold font-mono text-green-400">
                          {formatCurrency(scratchSettlementReport.reduce((a, s) => a + (s.total_value || 0), 0))}
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            {isFieldVisible('transaction_id') && <th className="text-left p-3 text-sm font-medium">ID</th>}
                            {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                            {isFieldVisible('total_cards') && <th className="text-right p-3 text-sm font-medium">Total Cards</th>}
                            {isFieldVisible('total_value') && <th className="text-right p-3 text-sm font-medium">Total Value</th>}
                            {isFieldVisible('entered_by') && <th className="text-left p-3 text-sm font-medium">Entered By</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {scratchSettlementReport.map((s, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                              {isFieldVisible('transaction_id') && <td className="p-3 text-sm font-mono">{s.transaction_id || '-'}</td>}
                              {isFieldVisible('date') && <td className="p-3 text-sm">{s.date}</td>}
                              {isFieldVisible('total_cards') && <td className="p-3 text-right font-mono text-sm">{s.total_cards?.toLocaleString()}</td>}
                              {isFieldVisible('total_value') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(s.total_value)}</td>}
                              {isFieldVisible('entered_by') && <td className="p-3 text-sm">{getUserName(s.user_id)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No settlements found</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Scratch Card Sales Report */}
          {reportType === 'scratch_sales' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Scratch Card Sales Report</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = scratchSalesReport.map(s => ({
                        date: s.date,
                        shift: s.shift?.toUpperCase(),
                        scratch_sales: s.scratch_sales,
                        scratch_prize_paid: s.scratch_prize_paid,
                        net_sales: s.scratch_sales - s.scratch_prize_paid,
                        submitted_by: getUserName(s.submitted_by)
                      }));
                      exportCSV(exportData, 'scratch_sales_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = scratchSalesReport.map(s => ({
                        date: s.date,
                        shift: s.shift?.toUpperCase(),
                        scratch_sales: s.scratch_sales,
                        scratch_prize_paid: s.scratch_prize_paid,
                        net_sales: s.scratch_sales - s.scratch_prize_paid,
                        submitted_by: getUserName(s.submitted_by)
                      }));
                      exportPDF(exportData, 'Scratch Card Sales Report', 'scratch_sales_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {scratchSalesReport.length > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Sales</p>
                        <p className="text-xl font-bold font-mono text-blue-400">
                          {formatCurrency(scratchSalesReport.reduce((a, s) => a + (s.scratch_sales || 0), 0))}
                        </p>
                      </div>
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Prizes Paid</p>
                        <p className="text-xl font-bold font-mono text-red-400">
                          {formatCurrency(scratchSalesReport.reduce((a, s) => a + (s.scratch_prize_paid || 0), 0))}
                        </p>
                      </div>
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Net Sales</p>
                        <p className="text-xl font-bold font-mono text-green-400">
                          {formatCurrency(
                            scratchSalesReport.reduce((a, s) => a + (s.scratch_sales || 0), 0) -
                            scratchSalesReport.reduce((a, s) => a + (s.scratch_prize_paid || 0), 0)
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                            {isFieldVisible('shift') && <th className="text-left p-3 text-sm font-medium">Shift</th>}
                            {isFieldVisible('scratch_sales') && <th className="text-right p-3 text-sm font-medium">Sales</th>}
                            {isFieldVisible('scratch_prize_paid') && <th className="text-right p-3 text-sm font-medium">Prize Paid</th>}
                            <th className="text-right p-3 text-sm font-medium">Net Sales</th>
                            {isFieldVisible('submitted_by') && <th className="text-left p-3 text-sm font-medium">Submitted By</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {scratchSalesReport.map((s, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                              {isFieldVisible('date') && <td className="p-3 text-sm">{s.date}</td>}
                              {isFieldVisible('shift') && <td className="p-3 text-sm uppercase">{s.shift}</td>}
                              {isFieldVisible('scratch_sales') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(s.scratch_sales)}</td>}
                              {isFieldVisible('scratch_prize_paid') && <td className="p-3 text-right font-mono text-sm text-red-400">{formatCurrency(s.scratch_prize_paid)}</td>}
                              <td className="p-3 text-right font-mono text-sm text-green-400">{formatCurrency(s.scratch_sales - s.scratch_prize_paid)}</td>
                              {isFieldVisible('submitted_by') && <td className="p-3 text-sm">{getUserName(s.submitted_by)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No scratch card sales found</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fuel Deliveries Report */}
          {reportType === 'fuel_deliveries' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Fuel Deliveries Report{selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredFuelDeliveryReport.map(d => ({
                        date: d.date,
                        grade: d.grade_name || '-',
                        quantity: d.quantity,
                        cost_per_litre: `${d.cost_per_litre?.toFixed(2)}p`,
                        total_cost: d.total_cost,
                        invoice_number: d.invoice_number || '-',
                        entered_by: getUserName(d.user_id)
                      }));
                      exportCSV(exportData, 'fuel_deliveries_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredFuelDeliveryReport.map(d => ({
                        date: d.date,
                        grade: d.grade_name || '-',
                        quantity: d.quantity,
                        cost_per_litre: `${d.cost_per_litre?.toFixed(2)}p`,
                        total_cost: d.total_cost,
                        invoice_number: d.invoice_number || '-',
                        entered_by: getUserName(d.user_id)
                      }));
                      exportPDF(exportData, 'Fuel Deliveries Report', 'fuel_deliveries_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {filteredFuelDeliveryReport.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Quantity Delivered{selectedFuelGrade !== 'all' ? ` (${selectedFuelGrade})` : ''}</p>
                        <p className="text-xl font-bold font-mono text-amber-400">
                          {filteredFuelDeliveryReport.reduce((a, d) => a + (d.quantity || 0), 0).toLocaleString()} L
                        </p>
                      </div>
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Cost{selectedFuelGrade !== 'all' ? ` (${selectedFuelGrade})` : ''}</p>
                        <p className="text-xl font-bold font-mono text-green-400">
                          {formatCurrency(filteredFuelDeliveryReport.reduce((a, d) => a + (d.total_cost || 0), 0))}
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                            {isFieldVisible('grade') && <th className="text-left p-3 text-sm font-medium">Grade</th>}
                            {isFieldVisible('quantity') && <th className="text-right p-3 text-sm font-medium">Quantity (L)</th>}
                            {isFieldVisible('cost_per_litre') && <th className="text-right p-3 text-sm font-medium">Cost/L</th>}
                            {isFieldVisible('total_cost') && <th className="text-right p-3 text-sm font-medium">Total Cost</th>}
                            {isFieldVisible('invoice_number') && <th className="text-left p-3 text-sm font-medium">Invoice #</th>}
                            {isFieldVisible('entered_by') && <th className="text-left p-3 text-sm font-medium">Entered By</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFuelDeliveryReport.map((d, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                              {isFieldVisible('date') && <td className="p-3 text-sm">{d.date}</td>}
                              {isFieldVisible('grade') && <td className="p-3 text-sm">{d.grade_name || '-'}</td>}
                              {isFieldVisible('quantity') && <td className="p-3 text-right font-mono text-sm">{d.quantity?.toLocaleString()}</td>}
                              {isFieldVisible('cost_per_litre') && <td className="p-3 text-right font-mono text-sm">{d.cost_per_litre?.toFixed(2)}p</td>}
                              {isFieldVisible('total_cost') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(d.total_cost)}</td>}
                              {isFieldVisible('invoice_number') && <td className="p-3 text-sm">{d.invoice_number || '-'}</td>}
                              {isFieldVisible('entered_by') && <td className="p-3 text-sm">{getUserName(d.user_id)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No fuel deliveries found{selectedFuelGrade !== 'all' ? ` for ${selectedFuelGrade}` : ''}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fuel Sales Report */}
          {reportType === 'fuel_sales' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Fuel Sales Report</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredFuelSalesReport.map(s => {
                        const row = {
                          date: s.date,
                          shift: s.shift?.toUpperCase(),
                          total_volume: s.total_volume,
                          total_sales: s.total_sales,
                          submitted_by: getUserName(s.submitted_by)
                        };
                        if (selectedFuelGrade !== 'all') {
                          row.grade = s.grade_name || '-';
                          row.price_per_litre = `${s.price_per_litre?.toFixed(1)}p`;
                        }
                        return row;
                      });
                      exportCSV(exportData, 'fuel_sales_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = filteredFuelSalesReport.map(s => {
                        const row = {
                          date: s.date,
                          shift: s.shift?.toUpperCase(),
                          total_volume: s.total_volume,
                          total_sales: s.total_sales,
                          submitted_by: getUserName(s.submitted_by)
                        };
                        if (selectedFuelGrade !== 'all') {
                          row.grade = s.grade_name || '-';
                          row.price_per_litre = `${s.price_per_litre?.toFixed(1)}p`;
                        }
                        return row;
                      });
                      exportPDF(exportData, `Fuel Sales Report${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`, 'fuel_sales_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {filteredFuelSalesReport.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Volume Sold{selectedFuelGrade !== 'all' ? ` (${selectedFuelGrade})` : ''}</p>
                        <p className="text-xl font-bold font-mono text-amber-400">
                          {filteredFuelSalesReport.reduce((a, s) => a + (s.total_volume || 0), 0).toLocaleString()} L
                        </p>
                      </div>
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Sales{selectedFuelGrade !== 'all' ? ` (${selectedFuelGrade})` : ''}</p>
                        <p className="text-xl font-bold font-mono text-green-400">
                          {formatCurrency(filteredFuelSalesReport.reduce((a, s) => a + (s.total_sales || 0), 0))}
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            {isFieldVisible('date') && <th className="text-left p-3 text-sm font-medium">Date</th>}
                            {isFieldVisible('shift') && <th className="text-left p-3 text-sm font-medium">Shift</th>}
                            {selectedFuelGrade !== 'all' && isFieldVisible('grade') && <th className="text-left p-3 text-sm font-medium">Grade</th>}
                            {isFieldVisible('total_volume') && <th className="text-right p-3 text-sm font-medium">Volume (L)</th>}
                            {selectedFuelGrade !== 'all' && isFieldVisible('price_per_litre') && <th className="text-right p-3 text-sm font-medium">Price/L</th>}
                            {isFieldVisible('total_sales') && <th className="text-right p-3 text-sm font-medium">Sales</th>}
                            {isFieldVisible('submitted_by') && <th className="text-left p-3 text-sm font-medium">Submitted By</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFuelSalesReport.map((s, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                              {isFieldVisible('date') && <td className="p-3 text-sm">{s.date}</td>}
                              {isFieldVisible('shift') && <td className="p-3 text-sm uppercase">{s.shift}</td>}
                              {selectedFuelGrade !== 'all' && isFieldVisible('grade') && <td className="p-3 text-sm">{s.grade_name || '-'}</td>}
                              {isFieldVisible('total_volume') && <td className="p-3 text-right font-mono text-sm">{s.total_volume?.toLocaleString()}</td>}
                              {selectedFuelGrade !== 'all' && isFieldVisible('price_per_litre') && <td className="p-3 text-right font-mono text-sm">{s.price_per_litre?.toFixed(1)}p</td>}
                              {isFieldVisible('total_sales') && <td className="p-3 text-right font-mono text-sm">{formatCurrency(s.total_sales)}</td>}
                              {isFieldVisible('submitted_by') && <td className="p-3 text-sm">{getUserName(s.submitted_by)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No fuel sales found{selectedFuelGrade !== 'all' ? ` for ${selectedFuelGrade}` : ''}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Fuel Price History Report */}
          {reportType === 'fuel_prices' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Fuel Price History</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = (selectedFuelGrade === 'all' 
                        ? fuelPriceReport 
                        : fuelPriceReport.filter(p => p.grade_name === selectedFuelGrade)
                      ).map(p => ({
                        date: p.date,
                        store: p.store_name,
                        grade: p.grade_name,
                        cost_price: p.cost_price?.toFixed(2),
                        selling_price: p.selling_price?.toFixed(2),
                        margin: p.margin
                      }));
                      exportCSV(exportData, 'fuel_price_history');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = (selectedFuelGrade === 'all' 
                        ? fuelPriceReport 
                        : fuelPriceReport.filter(p => p.grade_name === selectedFuelGrade)
                      ).map(p => ({
                        date: p.date,
                        store: p.store_name,
                        grade: p.grade_name,
                        cost_price: `${p.cost_price?.toFixed(2)}p`,
                        selling_price: `${p.selling_price?.toFixed(2)}p`,
                        margin: `${p.margin}p`
                      }));
                      exportPDF(exportData, `Fuel Price History${selectedFuelGrade !== 'all' ? ` - ${selectedFuelGrade}` : ''}`, 'fuel_price_history');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const filteredPrices = selectedFuelGrade === 'all' 
                    ? fuelPriceReport 
                    : fuelPriceReport.filter(p => p.grade_name === selectedFuelGrade);
                  
                  if (filteredPrices.length > 0) {
                    // Group by date for better visualization
                    const groupedByDate = filteredPrices.reduce((acc, p) => {
                      if (!acc[p.date]) acc[p.date] = [];
                      acc[p.date].push(p);
                      return acc;
                    }, {});
                    
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <p className="text-sm text-muted-foreground">Total Price Updates</p>
                            <p className="text-xl font-bold font-mono text-blue-400">
                              {filteredPrices.length}
                            </p>
                          </div>
                          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-sm text-muted-foreground">Days with Updates</p>
                            <p className="text-xl font-bold font-mono text-amber-400">
                              {Object.keys(groupedByDate).length}
                            </p>
                          </div>
                          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <p className="text-sm text-muted-foreground">Avg Margin (Latest)</p>
                            <p className="text-xl font-bold font-mono text-green-400">
                              {(() => {
                                const latestDate = Object.keys(groupedByDate).sort().reverse()[0];
                                if (!latestDate) return '-';
                                const latestPrices = groupedByDate[latestDate];
                                const avgMargin = latestPrices.reduce((sum, p) => sum + (parseFloat(p.margin) || 0), 0) / latestPrices.length;
                                return `${avgMargin.toFixed(2)}p`;
                              })()}
                            </p>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-border">
                                <th className="text-left p-3 text-sm font-medium">Date</th>
                                <th className="text-left p-3 text-sm font-medium">Store</th>
                                <th className="text-left p-3 text-sm font-medium">Grade</th>
                                <th className="text-right p-3 text-sm font-medium">Cost Price</th>
                                <th className="text-right p-3 text-sm font-medium">Selling Price</th>
                                <th className="text-right p-3 text-sm font-medium">Margin</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredPrices.map((p, idx) => (
                                <tr key={p.id || idx} className="border-b border-border/50 hover:bg-muted/50">
                                  <td className="p-3 text-sm">{p.date}</td>
                                  <td className="p-3 text-sm">{p.store_name}</td>
                                  <td className="p-3 text-sm">{p.grade_name}</td>
                                  <td className="p-3 text-right font-mono text-sm">{p.cost_price?.toFixed(2)}p</td>
                                  <td className="p-3 text-right font-mono text-sm">{p.selling_price?.toFixed(2)}p</td>
                                  <td className="p-3 text-right font-mono text-sm text-green-400">{p.margin}p</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  } else {
                    return (
                      <p className="text-center text-muted-foreground py-8">
                        No fuel price updates found for the selected date range
                        {selectedFuelGrade !== 'all' ? ` and grade (${selectedFuelGrade})` : ''}.
                        <br />
                        <span className="text-sm">Price updates are recorded when you enter daily fuel prices in Sales Entry.</span>
                      </p>
                    );
                  }
                })()}
              </CardContent>
            </Card>
          )}

          {/* Fuel Profit Report */}
          {reportType === 'fuel_profit' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Fuel Profit Report</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = [];
                      fuelProfitReport.forEach(entry => {
                        entry.grades.forEach(g => {
                          exportData.push({
                            date: entry.date,
                            shift: entry.shift === 'dayend' ? 'Day End' : 'Day',
                            store: entry.store_name,
                            grade: g.grade_name,
                            volume: g.volume.toFixed(2),
                            cost_ppl: g.cost.toFixed(3),
                            price_ppl: g.price.toFixed(3),
                            margin_ppl: g.margin.toFixed(3),
                            profit: g.profit.toFixed(2)
                          });
                        });
                      });
                      exportCSV(exportData, 'fuel_profit_report');
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const exportData = [];
                      fuelProfitReport.forEach(entry => {
                        entry.grades.forEach(g => {
                          exportData.push({
                            date: entry.date,
                            shift: entry.shift === 'dayend' ? 'Day End' : 'Day',
                            store: entry.store_name,
                            grade: g.grade_name,
                            volume: `${g.volume.toFixed(2)}L`,
                            cost_ppl: `${g.cost.toFixed(3)}p`,
                            price_ppl: `${g.price.toFixed(3)}p`,
                            margin_ppl: `${g.margin.toFixed(3)}p`,
                            profit: `£${g.profit.toFixed(2)}`
                          });
                        });
                      });
                      exportPDF(exportData, 'Fuel Profit Report', 'fuel_profit_report');
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {fuelProfitReport.length > 0 ? (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Profit</p>
                        <p className="text-2xl font-bold font-mono text-green-400">
                          £{fuelProfitReport.reduce((sum, e) => sum + e.total_profit, 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Volume</p>
                        <p className="text-2xl font-bold font-mono text-blue-400">
                          {fuelProfitReport.reduce((sum, e) => sum + e.total_volume, 0).toFixed(0)}L
                        </p>
                      </div>
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Avg Profit/Day</p>
                        <p className="text-2xl font-bold font-mono text-amber-400">
                          £{(fuelProfitReport.reduce((sum, e) => sum + e.total_profit, 0) / (fuelProfitReport.length || 1)).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">Entries</p>
                        <p className="text-2xl font-bold font-mono text-purple-400">
                          {fuelProfitReport.length}
                        </p>
                      </div>
                    </div>

                    {/* Profit by Grade Summary */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Profit by Grade</h4>
                      <div className="grid grid-cols-4 gap-3">
                        {(() => {
                          const gradeProfit = {};
                          const gradeVolume = {};
                          fuelProfitReport.forEach(entry => {
                            entry.grades.forEach(g => {
                              if (!gradeProfit[g.grade_name]) {
                                gradeProfit[g.grade_name] = 0;
                                gradeVolume[g.grade_name] = 0;
                              }
                              gradeProfit[g.grade_name] += g.profit;
                              gradeVolume[g.grade_name] += g.volume;
                            });
                          });
                          return Object.keys(gradeProfit).map(gradeName => (
                            <div key={gradeName} className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
                              <p className="text-xs text-muted-foreground">{gradeName}</p>
                              <p className="text-lg font-bold font-mono text-green-400">£{gradeProfit[gradeName].toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">{gradeVolume[gradeName].toFixed(0)}L</p>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>

                    {/* Daily Details Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Shift</th>
                            <th className="text-left p-3">Store</th>
                            <th className="text-left p-3">Grade</th>
                            <th className="text-right p-3">Volume (L)</th>
                            <th className="text-right p-3">Cost (ppl)</th>
                            <th className="text-right p-3">Price (ppl)</th>
                            <th className="text-right p-3">Margin (ppl)</th>
                            <th className="text-right p-3">Profit (£)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fuelProfitReport.map((entry, entryIdx) => (
                            entry.grades.map((g, gradeIdx) => (
                              <tr key={`${entryIdx}-${gradeIdx}`} className="border-b border-slate-800 hover:bg-slate-800/30">
                                {gradeIdx === 0 ? (
                                  <>
                                    <td className="p-3" rowSpan={entry.grades.length}>{entry.date}</td>
                                    <td className="p-3" rowSpan={entry.grades.length}>{entry.shift === 'dayend' ? 'Day End' : 'Day'}</td>
                                    <td className="p-3" rowSpan={entry.grades.length}>{entry.store_name}</td>
                                  </>
                                ) : null}
                                <td className="p-3">{g.grade_name}</td>
                                <td className="p-3 text-right font-mono">{g.volume.toFixed(2)}</td>
                                <td className="p-3 text-right font-mono text-orange-400">{g.cost.toFixed(3)}</td>
                                <td className="p-3 text-right font-mono text-blue-400">{g.price.toFixed(3)}</td>
                                <td className="p-3 text-right font-mono text-amber-400">{g.margin.toFixed(3)}</td>
                                <td className="p-3 text-right font-mono text-green-400">£{g.profit.toFixed(2)}</td>
                              </tr>
                            ))
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-800/50">
                          <tr className="font-bold">
                            <td className="p-3" colSpan={4}>TOTAL</td>
                            <td className="p-3 text-right font-mono">{fuelProfitReport.reduce((sum, e) => sum + e.total_volume, 0).toFixed(2)}</td>
                            <td className="p-3" colSpan={3}></td>
                            <td className="p-3 text-right font-mono text-green-400">£{fuelProfitReport.reduce((sum, e) => sum + e.total_profit, 0).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No fuel profit data found for the selected period.</p>
                    <p className="text-sm mt-2">Make sure sales entries have fuel data and margins have been calculated.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              Send Report via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-recipient">Recipient Email</Label>
              {/* Show creditor email dropdown for Lending report */}
              {reportType === 'lending' && creditors.filter(c => c.email).length > 0 ? (
                <div className="space-y-2">
                  <Select 
                    value={emailRecipient} 
                    onValueChange={(val) => setEmailRecipient(val === 'custom' ? '' : val)}
                  >
                    <SelectTrigger data-testid="creditor-email-select">
                      <SelectValue placeholder="Select creditor email or enter custom" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Enter custom email...</SelectItem>
                      {creditors.filter(c => c.email && c.is_active).map(creditor => (
                        <SelectItem key={creditor.id} value={creditor.email}>
                          {creditor.name} ({creditor.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(!emailRecipient || emailRecipient === 'custom' || !creditors.some(c => c.email === emailRecipient)) && (
                    <Input
                      id="email-recipient"
                      type="email"
                      placeholder="Enter email address"
                      value={emailRecipient === 'custom' ? '' : emailRecipient}
                      onChange={(e) => setEmailRecipient(e.target.value)}
                      data-testid="email-recipient-input"
                    />
                  )}
                </div>
              ) : (
                <Input
                  id="email-recipient"
                  type="email"
                  placeholder="Enter email address"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  data-testid="email-recipient-input"
                />
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              <p>The report will be sent as a PDF attachment with the following details:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Report: {generateShareContent(reportType).title}</li>
                <li>Store: {getSelectedStoreName()}</li>
                <li>Date Range: {format(dateRange.start, 'dd/MM/yyyy')} - {format(dateRange.end, 'dd/MM/yyyy')}</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendReportEmail} 
              disabled={sendingEmail || !emailRecipient || emailRecipient === 'custom'}
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
